/**
 * Aureliseé skin analysis.
 *
 * Opent over het hele scherm als iemand op de pagina skin-analysis een link naar die
 * pagina aanklikt (de knop START ANALYSIS in de hero), bij #analysis in het adres, of
 * via een knop met data-au-sa-open. Vraag voor vraag, met een lijn bovenaan die mee
 * schuift. Aan het eind de uitslag: je huid, wat bij je past, de routine van drie, de
 * volledige routine en (als het aan staat) mailen. Met #analysis-example in het adres
 * (of in de editor, instelling editor_view) opent meteen een voorbeelduitslag.
 *
 * Wat waar staat:
 * - De vragen, antwoorden en rekenregels: hieronder. Uitleg per vraag in de HQ-map,
 *   webshop/skin-analysis-vragen.md. Nagerekend in de proefversie op 16 september 2026.
 * - Beelden, tekststijlen, kleurenschema, knopteksten, drempels en de mailoptie: de
 *   instellingen van sections/au-skin-analysis.liquid.
 * - De productgegevens (titel, prijs, variant, foto, de korte regel uit product_name)
 *   komen uit de sectie, dus uit de winkel.
 * - Toevoegen aan het mandje gaat zoals in au-ritual.js: /cart/add.js met de secties
 *   van het mandje, dan de gebeurtenis CartAddEvent. Vessel opent zelf de lade.
 * - Taal: de sectielabels vertaalt Shopify zelf. De teksten in dit bestand (vragen,
 *   antwoorden, uitleg, uitslag) staan hier in het Engels, met het Nederlands in het
 *   woordenboek NL in hoofdstuk 3b. Begint de taal van de pagina (html lang) met nl,
 *   dan geeft t() het Nederlands, anders het Engels zoals het er staat.
 *
 * Zie snippets/au-bouwlog.liquid.
 */

import { CartAddEvent } from '@theme/events';

const ROOT = document.querySelector('[data-au-sa-root]');

/* ------------------------------------------------------------------ */
/* 1. Instellingen en producten uit de sectie                          */
/* ------------------------------------------------------------------ */

/**
 * De instellingen uit de sectie. In de editor opnieuw na elke wijziging (shopify:section:load, 31 oktober 2026), zodat
 * de test en de voorbeelduitslag meteen de nieuwe teksten en stijlen tonen.
 * @param {Element | null} root
 */
function leesInstellingen(root) {
  const el = root?.querySelector('[data-au-sa-config]');
  try {
    return el ? JSON.parse(el.textContent || '{}') : null;
  } catch (problem) {
    console.error('[au-skin-analysis] instellingen niet te lezen', problem);
    return null;
  }
}

/** @type {any} */
let CFG = leesInstellingen(ROOT);

const HANDLES = {
  foam: 'the-radiant-cloud-foam-1',
  milk: 'the-gentle-milk-cleanser',
  oilcl: 'the-transforming-oil-cleanser',
  pore: 'the-pore-purifying-toner',
  hyal: 'the-hyaluronic-infusion-toner',
  polish: 'the-volcanic-sand-polish',
  aha: 'the-aha-peeling-drops-1',
  goldenc: 'the-golden-c-serum',
  peptide: 'the-peptide-renewal-serum',
  bioactive: 'the-bioactive-resilience-serum',
  bakuchiol: 'the-bakuchiol-renewal-oil-serum',
  ageless: 'the-ageless-renewal-serum',
  pigment: 'pigment-perfecting-serum',
  eyecream: 'the-dark-circle-eye-infusion',
  eyeserum: 'the-rejuvenating-eye-serum',
  daycream: 'the-age-defiance-day-cream',
  moisture: 'the-moisture-day-cream',
  night: 'the-planktonic-night-elixir',
  serenity: 'the-skin-serenity-cream',
  clarity: 'the-skin-clarity-cream',
  oil: 'the-skin-vitality-oil',
  stick: 'the-solar-defence-stick-spf50-invisible',
  fluid: 'sunscreen-spf30-with-tint',
  mask: 'clay-mask-for-pores-and-blackheads',
  booster: 'the-antioxidant-defense-booster',
};

/* Op welke klachten en huidtypes een product past. Klachten gelijk aan vraag 1,
   huidtypes gelijk aan custom.skintype. Bron: producten.md, blok "Voor de skin analysis". */
const FIT = {
  foam: [['blemishes', 'shine', 'pores'], ['normal', 'combo', 'oily']],
  milk: [['dryness', 'barrier'], ['dry', 'normal']],
  oilcl: [[], ['dry', 'normal', 'combo']],
  pore: [['blemishes', 'blackheads', 'pores', 'shine'], ['oily', 'combo', 'normal']],
  hyal: [['dryness', 'redness', 'barrier'], ['dry', 'normal', 'combo', 'oily']],
  polish: [['texture', 'dullness'], ['normal', 'combo', 'dry']],
  aha: [['spots', 'texture', 'dullness'], ['normal', 'combo', 'oily']],
  goldenc: [['spots', 'dullness', 'dryness'], ['normal', 'dry', 'combo']],
  peptide: [['lines', 'firmness', 'dryness'], ['normal', 'dry']],
  bioactive: [['dryness', 'shine'], ['normal', 'combo', 'oily']],
  bakuchiol: [['lines', 'texture', 'dullness'], ['dry', 'normal']],
  ageless: [['lines', 'texture', 'spots'], ['normal', 'dry']],
  pigment: [['spots'], ['normal', 'combo']],
  eyecream: [['eyes'], ['normal', 'combo', 'oily']],
  eyeserum: [['eyes', 'lines'], ['normal', 'dry', 'combo']],
  daycream: [['dryness', 'lines', 'dullness'], ['dry', 'normal']],
  moisture: [['dryness'], ['normal', 'dry', 'combo']],
  night: [['dryness', 'lines'], ['dry', 'normal']],
  serenity: [['redness', 'barrier', 'dryness'], ['dry', 'normal', 'combo']],
  clarity: [['spots', 'dullness'], ['normal', 'dry', 'combo']],
  oil: [['dryness', 'dullness'], ['dry', 'normal']],
  stick: [[], ['normal', 'dry', 'combo']],
  fluid: [['spots'], ['normal', 'dry', 'combo']],
  mask: [['blackheads', 'pores', 'blemishes', 'shine'], ['oily', 'combo', 'normal']],
  booster: [['dullness'], ['normal', 'combo', 'oily']],
};

/* De collectie van elk product (Jesse, 5 oktober 2026; webshop/skin-analysis-collecties.md). In een ritual staan nooit
   twee producten uit dezelfde collectie, ook niet als iemand zelf producten toevoegt. Bij de moisturizers mag een
   dagcrème samen met een nachtcrème: die hebben daarom elk een eigen collectie (day, night). De olie is een eigen
   collectie en telt als laatste stap, net als een moisturizer. Suncare komt alleen als vierde product (of later). De
   gewone maat staat hierboven in HANDLES; de 15 ml van de dagcrèmes en de olie komt nooit in een ritual. */
const COLL = {
  foam: 'cleanser', milk: 'cleanser', oilcl: 'cleanser', pore: 'toner', hyal: 'toner', polish: 'exfoliant', aha: 'exfoliant',
  goldenc: 'serum', peptide: 'serum', bioactive: 'serum', bakuchiol: 'serum', ageless: 'serum', pigment: 'serum',
  eyecream: 'eye', eyeserum: 'eye', daycream: 'day', moisture: 'day', serenity: 'day', night: 'night', clarity: 'night',
  oil: 'oil', stick: 'sun', fluid: 'sun', mask: 'mask', booster: 'booster',
};
/* Wanneer elk product gebruikt wordt (31 oktober 2026): morning, evening, both (ALL DAY) of week (niet elke dag: de
   scrub, de peeling en het masker). Uit when_to_use_titel en when_to_use_tekst in producten.md. Hier per sleutel en
   niet uit de winkel, want daar zijn die velden per taal vertaald ("avond", "ochtend en avond"). */
const MOMENT_OF = {
  foam: 'both', milk: 'both', oilcl: 'evening', pore: 'evening', hyal: 'both', polish: 'week', aha: 'week',
  goldenc: 'morning', peptide: 'both', bioactive: 'both', bakuchiol: 'evening', ageless: 'evening', pigment: 'evening',
  eyecream: 'both', eyeserum: 'evening', daycream: 'morning', moisture: 'morning', night: 'evening', serenity: 'both',
  clarity: 'evening', oil: 'both', stick: 'morning', fluid: 'morning', mask: 'week', booster: 'both',
};
/* Nooit twee oliën in een ritual: het olieserum en de gezichtsolie (Jesse, 5 oktober 2026). */
const OILS = ['bakuchiol', 'oil'];
/* De stap waar een product bij hoort (producten.md); masker en booster horen bij geen stap. */
const STEP_OF = {
  foam: 1, milk: 1, oilcl: 1, pore: 1, hyal: 1, polish: 2, aha: 2, goldenc: 2, peptide: 2, bioactive: 2, bakuchiol: 2,
  ageless: 2, pigment: 2, eyecream: 2, eyeserum: 2, daycream: 3, moisture: 3, serenity: 3, night: 3, clarity: 3, oil: 3,
  stick: 3, fluid: 3, mask: 0, booster: 0,
};
/** Mag dit product bij deze producten in één ritual? */
function fits(list, product) {
  if (!product) return false;
  const keys = list.filter(Boolean).map((p) => p.key);
  if (keys.includes(product.key)) return false;
  if (keys.some((k) => COLL[k] === COLL[product.key])) return false;
  if (OILS.includes(product.key) && keys.some((k) => OILS.includes(k))) return false;
  return true;
}
/* Op de Amerikaanse site verkopen we geen suncare (Jesse, 5 oktober 2026), zodra die online staat. */
const US_SITE = String((window.Shopify && window.Shopify.country) || '').toUpperCase() === 'US';

/** @typedef {{key:string, handle:string, title:string, url:string, price:number, variant:number, available:boolean, image:string|null, alt:string, line:string, when:string, inside:string, use:string, concerns:string[], skins:string[]}} Product */

/** @type {Record<string, Product>} */
const P = {};
/** @type {Record<string, string>} */
const L = {};
/** @type {Record<string, string>} */
const S = {};

/** Producten, teksten en stijlen uit CFG. Opnieuw na een wijziging in de editor. */
function gebruikInstellingen() {
  for (const [key, handle] of Object.entries(HANDLES)) {
    const data = CFG?.products?.[handle];
    P[key] = {
      key,
      handle,
      title: data?.title || handle,
      url: data?.url || '#',
      price: Number(data?.price || 0),
      variant: Number(data?.variant || 0),
      available: Boolean(data && data.available && data.variant),
      image: data?.image || null,
      alt: data?.alt || data?.title || '',
      line: data?.line || '',
      when: data?.when || '',
      inside: data?.inside || '',
      use: data?.use || '',
      concerns: FIT[key][0],
      skins: FIT[key][1],
    };
  }
  for (const object of [L, S]) for (const key of Object.keys(object)) delete object[key];
  Object.assign(L, CFG?.labels || {});
  Object.assign(S, CFG?.styles || {});
}
gebruikInstellingen();

/* ------------------------------------------------------------------ */
/* 2. De vragen                                                        */
/* ------------------------------------------------------------------ */

const CONCERNS = [
  ['spots', 'Dark spots and marks'],
  ['lines', 'Fine lines and wrinkles'],
  ['firmness', 'Loss of firmness'],
  ['redness', 'Redness and sensitivity'],
  ['barrier', 'A tight, damaged feeling'],
  ['dryness', 'Dryness or dehydration'],
  ['dullness', 'Dullness'],
  ['texture', 'Rough or uneven texture'],
  ['blemishes', 'Blemishes'],
  ['blackheads', 'Blackheads'],
  ['pores', 'Visible pores'],
  ['shine', 'Shiny areas'],
  ['eyes', 'Eye bags and dark circles'],
];
const CNAME = Object.fromEntries(CONCERNS.map(([v, t]) => [v, t.toLowerCase()]));
const has = (list, value) => Array.isArray(list) && list.includes(value);
const opts = (list) => list.map(([v, t]) => ({ v, t }));

/** @typedef {{id:string, block:number, kind:'single'|'multi'|'parts', key:string, text:string, why:string, when?:(s:any)=>boolean, one?:string[], hint?:string, parts?:any[], opts?:any}} Question */

/*
 * De korte versie (Jesse, 5 oktober 2026): hoogstens 10 vragen voor iedereen, ook met de vervolgvraag meegeteld.
 * Weg of samengevoegd: 3f (de uitslag gebruikte het antwoord nergens), 3i (nu een keuze in 3g), het huidtype in één
 * vraag (was 4, 4b en 4c), parfum (zit in de laatste vraag), huidverzorging nu en exfoliëren samen, de laatste stap
 * en wanneer samen, de oogzone (nu twee keuzes in vraag 2), hoe je jezelf omschrijft en zwangerschap samen (vervangt
 * het besluit van 16 september 2026), zon en make-up samen. De rekenregels zijn niet veranderd: normalize() zet de
 * nieuwe antwoorden om naar wat build() al kende. Uitleg: webshop/skin-analysis-vragen.md.
 */
/** @type {Question[]} */
const Q = [
  { id: '1', block: 0, kind: 'single', key: 'top', text: 'What is your top skin concern?',
    why: 'This tells us which ritual to build. Everything after it makes that ritual yours.',
    opts: () => opts(CONCERNS).concat([{ v: 'none', t: 'I do not have any particular concern' }]) },
  { id: '2', block: 0, kind: 'multi', key: 'more', text: 'Do you have other concerns?',
    need: 'Choose at least one, or that there is nothing else.',
    why: 'Second concerns decide the fourth product and the swaps inside your ritual.',
    opts: (s) => opts(CONCERNS.filter(([v]) => v !== s.top))
      .concat(s.top === 'eyes' ? [] : [{ v: 'eyelines', t: 'Fine lines around the eyes' }])
      .concat([{ v: 'none', t: 'I do not have other concerns' }]) },

  /* De vervolgvraag noemt altijd letterlijk de hoofdklacht uit vraag 1 (Jesse, 5 oktober 2026): wie bij vraag 1 en 2
     meerdere dingen koos, weet zo waar de vraag over gaat. */
  { id: '3a', block: 1, kind: 'multi', key: 'why', when: (s) => s.top === 'spots',
    text: 'Where do your dark spots and marks come from?',
    why: 'Marks from the sun ask for something different than marks left behind by a blemish.',
    opts: opts([['sun', 'Sun exposure over the years'], ['post', 'They stay behind after a blemish'],
      ['tone', 'My tone is uneven all over, without clear spots'], ['recent', 'They appeared recently'], ['unknown', 'I do not know']]) },
  { id: '3b', block: 1, kind: 'multi', key: 'why', when: (s) => s.top === 'lines' || s.top === 'firmness',
    text: (s) => (s.top === 'firmness' ? 'Where do you notice the loss of firmness most?' : 'Where do you see your fine lines and wrinkles most?'),
    why: 'The eye area asks for a different product than the rest of the face.',
    opts: opts([['eyes', 'Around the eyes'], ['brow', 'Forehead and between the brows'], ['mouth', 'Around the mouth'],
      ['firm', 'My face feels less firm overall'], ['all', 'A little everywhere']]) },
  { id: '3c', block: 1, kind: 'multi', key: 'why', when: (s) => s.top === 'redness',
    text: 'What does your skin do when the redness and sensitivity flare up?',
    why: 'Rough and tight points to the barrier, warm and red points to reactivity. Those are two rituals.',
    opts: opts([['red', 'It goes red and warm'], ['sting', 'It stings when I apply something'],
      ['rough', 'It feels rough and tight'], ['flake', 'It flakes'], ['weather', 'Cold and wind set it off']]) },
  { id: '3d', block: 1, kind: 'multi', key: 'why', when: (s) => s.top === 'barrier',
    text: 'What do you think set off the tight, damaged feeling?',
    why: 'If you use an acid or a retinol now, the first advice is to pause it.',
    opts: opts([['acid', 'Acids or scrubs'], ['retinol', 'A retinol or a plant alternative'],
      ['weather', 'Cold, wind or indoor heating'], ['newproduct', 'A new product'], ['unknown', 'I cannot tell']]) },
  { id: '3e', block: 1, kind: 'multi', key: 'why', when: (s) => s.top === 'dryness',
    text: 'How does your dryness or dehydration show?',
    why: 'Tight without flaking is a shortage of water. Flaking is a shortage of oil.',
    opts: opts([['tight', 'Tight right after cleansing'], ['flake', 'Flaky patches'], ['makeup', 'Make-up clings to dry spots'],
      ['dull', 'It looks dull with it'], ['winter', 'Only in winter']]) },
  { id: '3g', block: 1, kind: 'multi', key: 'why',
    when: (s) => ['blemishes', 'blackheads', 'pores', 'shine'].includes(s.top),
    text: (s) => ({ blemishes: 'Tell us more about your blemishes.', blackheads: 'Tell us more about your blackheads.',
      pores: 'Tell us more about your visible pores.', shine: 'Tell us more about your shiny areas.' }[s.top] || 'Tell us more about your skin.'),
    why: 'Blackheads ask for the mask. The odd blemish asks for the toner. Deep, painful blemishes go further than skincare reaches, and we would rather say so.',
    opts: opts([['jaw', 'Mostly on the chin and jawline'], ['tzone', 'Mostly forehead and nose'],
      ['blackheads', 'Blackheads are the main thing'], ['oily', 'My skin gets shiny during the day'],
      ['dryblemish', 'My skin is dry and blemish prone at the same time'],
      ['deep', 'Some are painful or sit deep under the skin']]) },
  { id: '3h', block: 1, kind: 'single', key: 'eyewhat', when: (s) => s.top === 'eyes',
    text: 'What bothers you most around the eyes?',
    why: 'Circles and puffiness ask for a different product than fine lines.',
    opts: opts([['circles', 'Dark circles'], ['bags', 'Puffiness and bags'], ['lines', 'Fine lines'], ['dry', 'Dryness'], ['all', 'All of it']]) },

  /* Vraag 4 (Jesse, 5 oktober 2026): "Hoe voelt jouw huid aan?", met klein eronder "Kies wat het meest bij je past." */
  { id: '4', block: 2, kind: 'single', key: 'skintype', text: 'How does your skin feel?', hint: 'Pick the one that fits best.',
    why: 'This is how we read your skin type. If you are not sure, we take the gentle route of a dry skin.',
    opts: opts([['dry', 'Tight after cleansing, hardly ever shiny, small pores'],
      ['normal', 'Comfortable after cleansing, no real shine, pores you have to look for'],
      ['combo', 'Shiny on the nose and forehead, comfortable on the cheeks'],
      ['oily', 'Shiny all over by the end of the day, pores you can see'],
      ['unknown', 'I do not know']]) },
  /* Vraag 5 in twee delen op één scherm (Jesse, 5 oktober 2026): eerst of de huid vaak reageert (ja, nee, weet ik niet),
     daaronder exfoliëren. Bij "ja" en "weet ik niet" vallen de keuzes over exfoliëren weg en komt de zachte route.
     Volgende werkt pas als alles beantwoord is; deze vraag gaat nooit vanzelf door. */
  /* Sinds 5 oktober 2026 (avond, Jesse): de vraag of de huid vaak reageert is de hoofdvraag; "Hoeveel kan je huid
     hebben?" is weg. Het deel over exfoliëren krijgt een eigen korte kop. */
  { id: '5', block: 2, kind: 'parts', key: 'take', text: 'Does your skin often react to new products (redness, itching or stinging)?',
    why: 'A skin that often reacts gets a gentle ritual, without acids, scrubs or two renewal actives at once. If you are not sure, we take that gentle route too. Otherwise you choose how you like to exfoliate.',
    parts: [
      { key: 'reacts', kind: 'single', text: '', need: 'Choose whether your skin often reacts.',
        opts: opts([['yes', 'Yes'], ['no', 'No'], ['unknown', 'I do not know']]) },
      { key: 'exfo', kind: 'single', when: (s) => s.reacts === 'no', head: 'Exfoliating', text: 'How do you feel about exfoliating?',
        need: 'Choose how you feel about exfoliating.',
        opts: opts([['acid', 'I would like a leave-on acid'], ['scrub', 'I would rather scrub'], ['either', 'Either is fine, you choose'],
          ['start', 'I am just starting out, keep it simple'], ['neverdone', 'I have never exfoliated'],
          ['tried', 'I tried acids and they were too much'], ['never', 'I would rather not exfoliate at all'],
          ['unknown', 'I do not know']]) },
    ] },
  /* Eén textuur, en "vooral 's avonds" mag erbij (Jesse, 5 oktober 2026: wie vooral 's avonds tijd heeft, krijgt bij
     lijntjes en droogte de nachtcrème als stap 3, ook bij "geen voorkeur" of een olie). */
  { id: '6', block: 3, kind: 'multi', key: 'finish', text: 'What should your last step feel like?',
    need: 'Choose what your last step should feel like.',
    why: 'The last step is the one you feel all day, so you get to choose it. If you do not know yet, we choose it for your skin. Tell us if your skin care is mostly an evening moment.',
    hint: 'Pick one, and add the evening if it fits.',
    one: ['rich', 'light', 'oil', 'any', 'unknown'],
    opts: opts([['rich', 'A rich cream'], ['light', 'A light cream'], ['oil', 'A facial oil'], ['any', 'No preference'],
      ['unknown', 'I do not know'], ['evening', 'I mostly take care of my skin in the evening']]) },

  { id: '7', block: 4, kind: 'single', key: 'preg', text: 'Are you pregnant or breastfeeding?',
    why: 'So we leave out ingredients you are better off not using while pregnant or breastfeeding.',
    opts: opts([['yes', 'Yes'], ['no', 'No'], ['na', 'This does not apply to me'], ['private', 'I would rather not say']]) },
  /* Vraag 8 in twee delen op één scherm (Jesse, 5 oktober 2026): bovenaan de zon (precies één, make-up mag erbij),
     daaronder "Moet er iets uit je routine?" (niets, of wat eruit moet). Volgende werkt pas als beide beantwoord zijn. */
  { id: '8', block: 4, kind: 'parts', key: 'last', text: 'Two last things',
    why: 'If you are hardly ever outside, we are not going to push sun protection into your ritual. Daily make-up or sun protection asks for an oil cleanser in the evening. Almond oil is in the facial oil and in the vitamin C serum, beeswax is in the tinted sun fluid, and without fragrance you get the only two formulas without it.',
    parts: [
      { key: 'day', kind: 'multi', one: ['indoors', 'some', 'outdoors', 'spf'], text: 'What does a normal day look like for your skin?',
        need: 'Choose how often you are in the sun.',
        hint: 'Pick one. Make-up can go with it.',
        opts: opts([['indoors', 'Indoors most days'], ['some', 'Some sun, in summer and on holiday'],
          ['outdoors', 'Outdoors most days'], ['spf', 'I already wear sun protection every day'],
          ['makeup', 'I wear make-up every day']]) },
      { key: 'avoid', kind: 'multi', text: 'Is there anything else we should take into account?',
        need: 'Choose whether we should take anything into account, or choose Nothing.',
        opts: opts([['none', 'Nothing'], ['fragrance', 'No fragrance'], ['nuts', 'No nuts, including almond'],
          ['vegan', 'Vegan'], ['gluten', 'Gluten free']]) },
    ] },
];

/**
 * De antwoorden van de korte versie omzetten naar wat de rekenregels kennen (5 oktober 2026), zodat build() niet
 * hoefde te veranderen. Een oude state (met skin, now, tex, when, sun, makeup) blijft zoals hij is.
 */
function normalize(input) {
  const s = { ...input };
  const more = Array.isArray(s.more) ? s.more : [];
  if (has(more, 'eyelines')) {
    s.eye = has(more, 'eyes') ? 'both' : 'lines';
    s.more = more.filter((x) => x !== 'eyelines');
  }
  if (has(s.why, 'deep')) s.deep = 'yes';
  /* Vraag 5 in twee delen (5 oktober 2026): "ja" en "weet ik niet" geven de zachte route, net als "vaak" voorheen. */
  if (s.reacts) {
    if (s.reacts === 'yes' || s.reacts === 'unknown') { s.react = 'often'; delete s.exfo; }
    else s.react = s.react || 'never';
  }
  /* "Nog nooit gedaan" en "weet ik niet" bij exfoliëren: de voorzichtige route. Geen exfoliant in de routine (wel
     eronder als tip, zoals bij een beginner), geen toner met zuur als stap één en geen crème met zuur als stap drie. */
  if (s.exfo === 'neverdone' || s.exfo === 'unknown') { s.exfo = 'start'; s.voorzichtig = true; }
  /* De vorige versie met 8 vragen (eerst die middag): reageren en exfoliëren in één keuze, dag en weglaten in één lijst. */
  if (s.take) {
    if (s.take === 'reacts') s.react = 'often';
    else { s.react = s.react || 'never'; s.exfo = s.take; }
  }
  if (Array.isArray(s.last)) {
    s.day = s.last.filter((x) => ['indoors', 'some', 'outdoors', 'spf', 'makeup'].includes(x));
    const weg = s.last.filter((x) => ['nuts', 'vegan', 'gluten', 'fragrance'].includes(x));
    s.avoid = weg.length ? weg : ['none'];
  }
  if (s.exfo === 'start') { s.exfo = 'either'; s.now = 'none'; }
  else if (s.exfo === 'acid') s.now = 'full';
  else if (s.exfo) s.now = 'basic';
  if (Array.isArray(s.finish)) {
    s.tex = s.finish.find((x) => ['rich', 'light', 'oil', 'any', 'unknown'].includes(x));
    if (s.tex === 'unknown') s.tex = 'any';   // weet ik niet: de laatste stap volgt de huid
    s.when = has(s.finish, 'evening') ? 'evening' : 'both';
  }
  if (Array.isArray(s.day)) {
    const sun = s.day.find((x) => ['indoors', 'some', 'outdoors', 'spf'].includes(x));
    s.sun = { indoors: 'none', some: 'some', outdoors: 'lots', spf: 'already' }[sun];
    s.makeup = has(s.day, 'makeup') || sun === 'spf' ? 'daily' : 'no';
  }
  return s;
}

/* ------------------------------------------------------------------ */
/* 3. De rekenregels                                                   */
/* ------------------------------------------------------------------ */

const GOALNAME = {
  pigment: 'Dark spots and an uneven tone', lines: 'Fine lines and firmness', sensitive: 'Skin that reacts easily',
  barrier: 'Skin that feels tight and worn', dry: 'Dryness and dehydration', dull: 'Dullness and texture',
  blemish: 'Blemishes and congestion', eyes: 'The eye area', basic: 'A calm everyday ritual',
};
const PLAN = {
  pigment: 'Dark spots take time. We work on them in the evening with Alpha-Arbutin and Bidens Pilosa, and keep the morning simple so the work is not undone.',
  lines: "Lines soften at the skin's own pace. A renewal active in the evening, a cream that holds moisture overnight, and nothing that works against it.",
  sensitive: 'Less is the whole plan. Cleanse gently, hydrate well, and leave out anything that gives your skin a reason to react.',
  barrier: 'Rest first. No acids, no scrubs, no renewal actives. Moisture, fats and time, until your skin feels like itself again.',
  dry: 'Water first, oil on top. A hydrating layer and a last step that holds it there.',
  dull: 'Dullness usually sits at the surface. Clear the surface, then feed it. That is what the steps below do.',
  blemish: 'Keep the pores clear without drying the rest out. Salicylic acid in the evening, light hydration, and a mask once or twice a week.',
  eyes: 'The skin around the eyes is thinner and shows it first. A product made for that zone, and a ritual that does not fight it.',
  basic: 'No particular concern is a good place to start. Three steps, nothing loud, and room to add something later.',
};
const TOGOAL = {
  spots: 'pigment', lines: 'lines', firmness: 'lines', redness: 'sensitive', barrier: 'barrier', dryness: 'dry',
  dullness: 'dull', texture: 'dull', blemishes: 'blemish', blackheads: 'blemish', pores: 'blemish', shine: 'blemish',
  eyes: 'eyes', none: 'basic',
};
const TYPENAME = { dry: 'Dry', normal: 'Normal', combo: 'Combination', oily: 'Oily' };
const TYPELINE = {
  dry: 'It stays tight after cleansing, it hardly ever shines, and the pores are small.',
  normal: 'Comfortable after cleansing, no shine to speak of, pores you have to look for.',
  combo: 'Shine across the nose and forehead, comfortable on the cheeks.',
  oily: 'Shine across the whole face by the end of the day, and pores you can see.',
};

/* ---------- De vinkjes in de uitslag (Jesse, 31 oktober 2026) ----------
 * "beschrijf echt iemand zijn huidtype per uitslag, maar ook wat hier aan te doen valt. waar moeten ze op letten, en hoe
 * lossen ze hun probleem op." Per huidtype wat het is en hoe je ermee omgaat; per hoofdklacht (vraag 1) wat er speelt,
 * wat helpt en waar je op let; per huidconditie die niet al de hoofdklacht is een korte tip. Claims volgens merkboek.md
 * hoofdstuk 6 en 7: wat de huid doet en wat jij doet, geen belofte van een eindtoestand, geen medische taal. */
const TYPECARE = {
  dry: 'Your skin makes less oil of its own, so it holds on to water less easily. That is why it feels tight after cleansing and hardly ever shines. Cleanse with lukewarm water rather than hot, and put your cream on while your skin is still slightly damp, so it keeps that water in.',
  normal: 'Your skin is in balance: enough oil to stay supple, not so much that it shines. The aim is to keep it that way. A mild cleanser, moisture morning and evening, and one new product at a time, so you always know what your skin likes.',
  combo: 'Your forehead, nose and chin make more oil than your cheeks, so one part of your face asks for less and the other for more. Cleanse gently, so your cheeks stay comfortable, and use a little less cream on the shiny areas than on the rest.',
  oily: 'Your skin makes more oil than it needs, which shows as shine during the day and pores you can see. It still needs moisture, just in a lighter texture. Cleanse morning and evening without scrubbing hard, and leave out anything that makes your skin feel stripped.',
};
const TYPEUNSURE = 'You were not sure, so we take the gentle route of a dry skin.';
const TOUCHY_NOTE = 'Your skin reacts easily, so no acids, no scrub, and never two renewal actives at once.';

/** Per hoofdklacht uit vraag 1: [kop, tekst, tekst zonder serum voor de korte parfumvrije route]. */
const CONCERNCARE = {
  spots: ['dark spots and marks', 'They are places where your skin made extra pigment, after sun or after a blemish, and they fade slowly, layer by layer. The serum in step two is the one that works on them, so use it every day. Sun protection in the morning matters just as much, because daylight darkens them again. Think in months rather than weeks.',
    'They are places where your skin made extra pigment, after sun or after a blemish, and they fade slowly, layer by layer. Sun protection in the morning counts most here, because daylight darkens them again. Think in months rather than weeks.'],
  lines: ['fine lines and wrinkles', 'Lines show first where your skin moves most and where it is driest, around the eyes and the mouth. They soften with regular use, not with more product. Use your serum every day, keep your skin moist morning and evening, and wear sun protection on the days you are outside, so you keep what you build up.',
    'Lines show first where your skin moves most and where it is driest, around the eyes and the mouth. Keep your skin moist morning and evening, and wear sun protection on the days you are outside, so you keep what you build up.'],
  firmness: ['loss of firmness', 'Firmness is how springy your skin feels. Skin that holds less moisture feels less firm, often along the jaw and the cheeks. Keep it supple with your serum every day and a cream that holds the moisture in, and protect it from the sun on the days you are outside.',
    'Firmness is how springy your skin feels. Skin that holds less moisture feels less firm, often along the jaw and the cheeks. Keep it supple with moisture morning and evening, and protect it from the sun on the days you are outside.'],
  redness: ['redness and sensitivity', 'Your skin turns red or stings quickly, from heat, cold, a new product or rubbing. The fewer reasons you give it, the calmer it stays. Use few products, lukewarm water and a soft towel to pat dry, and add one new product at a time, on a small area first.'],
  barrier: ['a tight, worn feeling', 'Skin that feels tight, rough or stings after cleansing is asking for rest. Acids, scrubs and strong cleansers take more from it than it can make up for. Leave them out for now, cleanse mildly, and give your skin moisture and fats morning and evening until it feels like itself again.'],
  dryness: ['dryness and dehydration', 'Dry skin is short of oil, dehydrated skin is short of water, and many people have both. You feel it as tightness, flaky patches or make-up that clings. Apply your products on slightly damp skin and finish with your cream, which keeps the water in. Lukewarm water instead of hot helps too.'],
  dullness: ['dullness', 'Skin looks dull when its surface is uneven or a little dry, so it reflects less light. The answer sits on that surface: clear it gently and keep it moist, so it smooths out. If you exfoliate, a few times a week is enough, and always follow with moisture.'],
  texture: ['rough or uneven texture', 'Texture is what you feel under your fingertips: small bumps, rough patches, a surface that is not smooth. It sits in the top layer of your skin, so gentle care works best here. If you exfoliate, a few times a week is enough, and moisture every day keeps the surface soft. Pressing harder does not work faster.'],
  blemishes: ['blemishes', 'A blemish starts in a pore that is blocked by oil and flakes of skin. The work is keeping pores clear without drying out the rest of your face. Cleanse morning and evening, change your pillowcase often, and leave blemishes alone: squeezing leaves marks that stay longer than the blemish itself.'],
  blackheads: ['blackheads', 'A blackhead is a pore filled with oil that turns dark where it meets the air, mostly on the nose and chin. Keeping those pores clear every day is what helps. Cleanse well in the evening, especially after make-up or sun protection, and leave squeezing alone: it irritates the skin around the pore.'],
  pores: ['visible pores', 'Pores are part of your skin, and how big they are is mostly a given. They stand out more when they are full of oil or when the skin around them is dry. Keep them clear with a thorough cleanse in the evening, keep your skin moist, and use lighter textures where you see them most.'],
  shine: ['shiny areas', 'Shine is oil on the surface, mostly on the forehead, nose and chin. Your skin needs some of that oil, so the aim is balance, not drying it out. Cleanse gently morning and evening, choose light textures, and blot during the day instead of washing more often.'],
  eyes: ['eye bags and dark circles', 'The skin around your eyes is thinner than elsewhere on your face, so tiredness, fluid and colour show there first. Puffiness is mostly fluid that settles overnight, and dark circles often run in the family. Use a product made for this zone, tap it in with your ring finger instead of rubbing, and wear sun protection around the eyes too.'],
  none: ['', 'Then the aim is to keep your skin as it is: clean, moist and protected. Three steps are enough for that. Add something only when your skin asks for it, and one product at a time, so you know what it does.'],
};

/** Per huidconditie: [kop, tekst, de hoofdklachten waar hij al onder valt]. */
const CONDCARE = {
  dehydrated: ['Short on water right now.', 'Any skin type can be, oily skin too: it feels tight even when it shines. Apply your products on slightly damp skin and finish with your cream, which keeps the water in.', ['dryness']],
  'easily upset': ['Quick to react.', 'That is why your ritual has no acids, no scrub and never two renewal actives at once. Keep the water lukewarm, pat dry instead of rubbing, and try anything new on a small area for a few days before it goes on your whole face.', ['redness']],
  'fragrance intolerant': ['Better without fragrance.', 'Everything in your ritual is without it. Keep it that way with the rest of what you put on your face.', []],
  'a barrier that needs rest': ['In need of rest.', 'Rough and tight means your skin is losing more moisture than it holds. Leave out acids, scrubs and renewal actives for now, and give it moisture and fats morning and evening.', ['barrier']],
  'dull at the surface': ['Dull at the surface.', 'That sits in the top layer of your skin. Moisture every day keeps the surface smooth, and if you exfoliate, a few times a week is enough.', ['dullness', 'texture']],
  congested: ['Congested in places.', 'Oil collects in the pores, mostly on the forehead, nose and chin. Cleanse thoroughly in the evening, especially after make-up or sun protection, and keep heavy textures away from those areas.', ['blemishes', 'blackheads', 'pores', 'shine']],
  'uneven in tone': ['Uneven in tone.', 'Spots and patches darken with daylight, so sun protection in the morning helps to keep them from getting darker on the days you are outside.', ['spots']],
};

/** Huidtype: sinds 5 oktober 2026 uit één vraag (vraag 4). Bij twijfel ("weet ik niet") bewust droog: de zachte route,
 * met de melkreiniger, want een droge huid met een schuimreiniger is erger dan andersom (Jesse, 5 oktober 2026). Een
 * oude state met de drie signalen (4, 4b en 4c) rekent nog zoals eerst; daar wint droog al bij een gelijke stand. */
function skinType(s) {
  if (s.skintype) return s.skintype in TYPENAME ? s.skintype : 'dry';
  const sc = { dry: 0, normal: 0, combo: 0, oily: 0 };
  if (s.skin in sc) sc[s.skin] += 2;
  if (s.shine === 'never') sc.dry += 2;
  else if (s.shine === 'none') { sc.dry += 1; sc.normal += 1; }
  else if (s.shine === 'tzone') sc.combo += 2;
  else if (s.shine === 'all') sc.oily += 2;
  if (s.pores === 'small') { sc.dry += 1; sc.normal += 1; }
  else if (s.pores === 'tzone') sc.combo += 1;
  else if (s.pores === 'wide') sc.oily += 2;
  let best = 'normal';
  let top = 0;
  for (const key of ['dry', 'normal', 'combo', 'oily']) if (sc[key] > top) { top = sc[key]; best = key; }
  return best;
}

function conditions(s, goal, touchy) {
  const out = [];
  const all = [s.top, ...(s.more || []).filter((x) => x !== 'none')];
  if (all.includes('dryness') || has(s.why, 'tight')) out.push('dehydrated');
  if (touchy || s.top === 'redness') out.push('easily upset');
  if (s.frag === 'bad' || has(s.avoid, 'fragrance')) out.push('fragrance intolerant');
  if (goal === 'barrier') out.push('a barrier that needs rest');
  if (all.includes('dullness') || all.includes('texture')) out.push('dull at the surface');
  if (all.some((c) => ['blemishes', 'blackheads', 'pores', 'shine'].includes(c))) out.push('congested');
  if (all.includes('spots')) out.push('uneven in tone');
  return out;
}

function build(input) {
  const s = normalize(input);
  const o = { notes: [], also: [], discount: true, tags: [], picks: [], four: null };
  /** @type {Record<string, boolean>} */
  const X = {};
  for (const key of Object.keys(P)) if (!P[key].available) X[key] = true;
  const block = (...keys) => keys.forEach((k) => { X[k] = true; });
  const ok = (k) => !X[k];
  if (US_SITE) block('stick', 'fluid');
  const more = (s.more || []).filter((x) => x !== 'none');
  const why = s.why || [];
  const avoid = s.avoid || [];
  let goal = TOGOAL[s.top] || 'basic';
  if (goal === 'sensitive' && (has(why, 'rough') || has(why, 'flake'))) goal = 'barrier';
  const st = skinType(s);
  o.goal = GOALNAME[goal];
  o.typename = TYPENAME[st];
  o.typeline = TYPELINE[st];
  o.typekey = st;
  o.typeUnsure = s.skintype === 'unknown';
  o.concern = s.top || 'none';
  o.plan = PLAN[goal];

  if (s.frag === 'bad' || has(avoid, 'fragrance')) {
    o.goal = 'Fragrance free, and kept short';
    o.conditions = conditions(s, 'sensitive', true);
    o.plan = 'Two products, and that is on purpose. These are the only two formulas in our range without any fragrance. Cleanse gently, hydrate well, and leave the rest alone until your skin feels settled.';
    o.one = P.hyal; o.three = P.serenity; o.discount = false;
    o.picks = [P.hyal, P.serenity];
    o.tags.push('Fragrance free');
    /* Ook hier de verwijzingen bij zwangerschap en bij diepe onzuiverheden (5 oktober 2026: die vielen in deze route weg,
       ook in de versie van 16 september). De twee producten staan niet in het zwangerschapsfilter. */
    if (s.preg === 'yes') {
      o.tags.push('Pregnancy');
      o.notes.push('We left out the acids, the retinol alternatives and the pigment actives. Talk to your doctor or midwife before you start anything new.');
    }
    if (s.preg === 'private' || s.gender === 'private') {
      o.notes.push('If you are pregnant or breastfeeding, talk to your doctor or midwife before you start anything new.');
    }
    if (s.deep === 'yes') o.notes.push('Deep, painful blemishes go further than skincare reaches. Please have someone look at them who is trained for it.');
    return o;
  }
  if (s.preg === 'yes') {
    block('pore', 'aha', 'bakuchiol', 'ageless', 'eyeserum', 'pigment', 'clarity');
    o.tags.push('Pregnancy');
    o.notes.push('We left out the acids, the retinol alternatives and the pigment actives. Talk to your doctor or midwife before you start anything new.');
  }
  if (s.preg === 'private' || s.gender === 'private') {
    o.notes.push('If you are pregnant or breastfeeding, talk to your doctor or midwife before you start anything new.');
  }
  const touchy = s.react === 'often' || s.react === 'always';
  if (touchy) {
    block('aha', 'polish', 'mask', 'pigment');
    o.tags.push('Gentle route');
    o.notes.push(TOUCHY_NOTE);
  }
  if (has(why, 'acid') || has(why, 'retinol') || s.exfo === 'tried') {
    block('aha', 'polish', 'mask');
    o.notes.push('Give your skin a rest first. No exfoliant while it feels like this.');
  }
  if (s.exfo === 'never') block('aha', 'polish', 'mask');
  if (has(avoid, 'nuts')) { block('oil', 'goldenc'); o.tags.push('Nut free'); }
  if (has(avoid, 'vegan')) { block('fluid'); o.tags.push('Vegan'); }
  if (has(avoid, 'gluten')) o.tags.push('Gluten free');

  o.one = goal === 'blemish' && ok('pore') && !touchy && !s.voorzichtig ? P.pore : st === 'dry' ? P.milk : P.foam;
  /* De oliereiniger (Jesse, 5 oktober 2026): wie elke dag make-up of zonbescherming draagt, krijgt hem als stap 1, in
     plaats van een andere reiniger, als hij door de filters komt (parfum, noten, zwangerschap, een huid die vaak
     reageert: hij heeft geen noten, is gemaakt voor een gevoelige huid en staat niet in het filter voor zwangerschap;
     wie parfum niet verdraagt, krijgt de korte route hierboven). Dan is er maar één reiniger, en hij komt niet meer als
     vierde product. Was stap 1 de toner met salicylzuur (onzuiverheden), dan wordt die het vierde product. */
  let tonerNaar4 = null;
  if (s.makeup === 'daily' && ok('oilcl')) {
    if (COLL[o.one.key] !== 'cleanser') tonerNaar4 = o.one;
    o.one = P.oilcl;
    o.notes.push('You wear make-up or sun protection every day, so you cleanse with the oil cleanser: it takes everything off in one go.');
  }

  let two = null;
  const alsoLines = has(more, 'lines') || has(more, 'firmness');
  if (goal === 'pigment') {
    if (alsoLines && ok('ageless')) two = P.ageless;
    else if (has(why, 'tone') && ok('goldenc')) two = P.goldenc;
    else if (ok('pigment')) two = P.pigment;
    else two = ok('goldenc') ? P.goldenc : P.peptide;
  } else if (goal === 'lines') {
    if (has(why, 'firm') || s.top === 'firmness' || touchy || !ok('ageless')) two = P.peptide;
    else if (s.tex === 'oil' && ok('bakuchiol')) two = P.bakuchiol;
    else two = P.ageless;
  } else if (goal === 'sensitive' || goal === 'barrier' || goal === 'basic') {
    two = P.peptide;
  } else if (goal === 'dry') {
    if (alsoLines && ok('bakuchiol') && !touchy) two = P.bakuchiol;
    else if (has(why, 'tight') && !has(why, 'flake') && !touchy) two = P.bioactive;
    else two = P.peptide;
  } else if (goal === 'dull') {
    if ((s.exfo === 'acid' || s.exfo === 'either') && ok('aha')) two = P.aha;
    else if (s.exfo === 'scrub' && ok('polish')) two = P.polish;
    else two = ok('goldenc') ? P.goldenc : P.peptide;
  } else if (goal === 'blemish') {
    two = touchy ? P.peptide : P.bioactive;
  } else if (goal === 'eyes') {
    two = s.eyewhat === 'lines' && ok('eyeserum') ? P.eyeserum : P.eyecream;
  }
  if ((s.now === 'random' || s.now === 'none') && (two === P.aha || two === P.polish)) {
    o.also.push(two);
    two = ok('goldenc') ? P.goldenc : P.peptide;
    o.notes.push('You are just starting out, so step two is a serum. The exfoliant can join once the rest is in place.');
  }
  o.two = two;

  const oilOK = ok('oil') && two !== P.bakuchiol;
  const evening = s.when === 'evening';
  let three;
  if (goal === 'sensitive' || goal === 'barrier') three = P.serenity;
  else if (goal === 'blemish') three = P.moisture;
  else if (goal === 'pigment') three = s.now === 'full' && ok('clarity') && !s.voorzichtig ? P.clarity : P.moisture;
  else if (goal === 'lines') three = evening ? P.night : P.daycream;
  else if (goal === 'dry') three = oilOK ? P.oil : evening ? P.night : P.daycream;
  else if (goal === 'dull') three = oilOK ? P.oil : P.moisture;
  else if (goal === 'eyes') three = st === 'dry' ? P.daycream : P.moisture;
  else three = P.moisture;
  if (s.tex === 'oil' && oilOK && !['sensitive', 'barrier', 'blemish'].includes(goal)) three = P.oil;
  if (s.tex === 'rich' && three === P.moisture && goal !== 'blemish') three = evening ? P.night : P.daycream;
  if (s.tex === 'light' && (three === P.night || three === P.daycream)) three = P.moisture;
  if (three === P.oil && !oilOK) three = P.moisture;
  o.three = three;

  const used = [o.one, o.two, o.three];
  const cands = [];
  if (tonerNaar4) cands.push([tonerNaar4, 'the salicylic acid toner works on your blemishes, after cleansing']);
  /* Het complete ritual klopt met de ochtend en de avond (Jesse, 31 oktober 2026: "de teksten en de routines moeten wel
     op elkaar aansluiten"). Gebruik je 's avonds iets maar is er geen crème voor de avond (stap drie is een dagcrème),
     dan is het vierde product een crème voor de avond; is er geen crème voor de ochtend, dan een crème voor de ochtend.
     Alleen een crème die bij je huidtype past en door de filters komt; de Skin Clarity Cream alleen bij vlekken of een
     doffe huid. */
  {
    const nu = used.filter(Boolean);
    const op = (p, m) => MOMENT_OF[p.key] === m || MOMENT_OF[p.key] === 'both';
    const gebruikt = (m) => nu.some((p) => op(p, m));
    const creme = (m) => nu.some((p) => ['day', 'night', 'oil'].includes(COLL[p.key]) && op(p, m));
    const alle = [s.top, ...more];
    const past = (p) => p && p.available && !X[p.key] && p.skins.includes(st) && fits(used, p);
    let gat = null;
    const avond = gebruikt('evening') && !creme('evening')
      ? [P.night, alle.some((c) => c === 'spots' || c === 'dullness') ? P.clarity : null].find(past) : null;
    /* De ochtend krijgt ook een crème als er 's ochtends nog niets is (alles in het ritual is voor de avond). */
    const ochtend = !creme('morning') ? [P.daycream, P.moisture, P.serenity].find(past) : null;
    if (avond) gat = [avond, 'your evening needs a cream of its own'];
    else if (ochtend) gat = [ochtend, 'your morning needs a cream of its own'];
    if (gat) cands.push(gat);
  }
  if ((s.eye && s.eye !== 'no') || has(more, 'eyes')) {
    if ((s.eye === 'lines' || s.eye === 'both') && ok('eyeserum')) cands.push([P.eyeserum, 'you asked about the eye area']);
    else cands.push([P.eyecream, 'you asked about the eye area']);
    if (s.eye === 'both' && ok('eyeserum')) cands.push([P.eyecream, 'after the eye serum']);
  }
  if (goal === 'eyes' && s.eyewhat === 'all') cands.push([two === P.eyeserum ? P.eyecream : P.eyeserum, 'the second step for the eye area']);
  if (s.sun === 'some') cands.push([ok('fluid') ? P.fluid : P.stick, 'you are in the sun now and then']);
  if (s.sun === 'lots') cands.push([P.stick, 'you are outdoors most days']);
  if ((has(more, 'blackheads') || has(more, 'pores') || has(why, 'blackheads')) && ok('mask')) cands.push([P.mask, 'for blackheads and pores']);
  const defaults = {
    pigment: [ok('goldenc') ? P.goldenc : P.hyal, 'the morning partner to your evening serum'],
    lines: [three === P.night ? P.daycream : P.night, 'so day and night each have their own cream'],
    sensitive: [P.hyal, 'a fragrance free layer of water after cleansing'],
    barrier: [has(why, 'flake') && ok('oil') ? P.oil : P.hyal, 'fats on top, or water underneath'],
    dry: [P.hyal, 'water first, oil on top'],
    dull: [ok('goldenc') ? P.goldenc : P.booster, 'antioxidants on top of your three steps'],
    blemish: [ok('mask') ? P.mask : P.foam, 'the one product that works on blackheads'],
  };
  cands.push(defaults[goal] || [P.hyal, 'a quiet layer of water under your cream']);
  const fallbacks = [[P.hyal, 'a layer of water after cleansing'], [P.night, 'a cream of its own for the night'],
    [P.daycream, 'a cream of its own for the morning'], [P.booster, 'antioxidants on top of your three steps']];
  /* Een product dat een filter tegenhoudt (zwanger, een huid die vaak reageert, noten, vegan) komt ook niet als vierde
     product of extra (5 oktober 2026: het oogserum kwam bij "alles rond de ogen" anders toch mee bij zwangerschap). */
  /* Het vierde product en de aanvullingen volgen de regels van de collecties (fits): nooit twee uit dezelfde collectie
     (een dagcrème en een nachtcrème mogen samen), nooit twee oliën. */
  for (const [product, reason] of cands) {
    if (!product || !product.available || X[product.key] || !fits(used, product)) continue;
    if (!o.four) { o.four = [product, reason]; used.push(product); }
    else if (!o.also.includes(product)) o.also.push(product);
  }
  for (const [product, reason] of fallbacks) {
    if (o.four) break;
    if (product.available && !X[product.key] && fits(used, product)) { o.four = [product, reason]; used.push(product); }
  }

  /* Wat bij je past: punten op klachten en huidtype. */
  const scored = [];
  for (const key of Object.keys(P)) {
    if (X[key]) continue;
    const p = P[key];
    let sc = 0;
    if (p.concerns.includes(s.top)) sc += 3;
    for (const c of more) if (p.concerns.includes(c)) sc += 2;
    sc += p.skins.includes(st) ? 1 : -3;
    if (p === o.one || p === o.two || p === o.three) sc += 4;
    if (o.four && p === o.four[0]) sc += 2;
    if (sc > 0) scored.push([sc, p]);
  }
  scored.sort((a, b) => b[0] - a[0]);
  /* Alle producten die passen, niet meer de beste zes (Jesse, 30 oktober 2026: "alle producten die geschikt zijn"). */
  o.picks = scored.map((x) => x[1]);
  for (const p of [o.one, o.two, o.three]) if (p && p.available && !o.picks.includes(p)) o.picks.push(p);

  /* Sinds ontwerp C (30 oktober 2026) staat o.also niet meer in de uitslag; de berekening blijft, zodat het terug kan.
     "Ook een goede aanvulling" (Jesse, 5 oktober 2026): producten die iemand zelf aan het complete ritual kan toevoegen,
     tot hoogstens 6 producten. Alleen wat bij het ritual mag (fits) en door de filters komt: eerst de andere kandidaten,
     dan de vaste aanvullers, dan wat het best bij de huid past. Wat iemand toevoegt, toetst de uitslag opnieuw. */
  const extra = [...o.also, ...fallbacks.map((x) => x[0]), ...scored.map((x) => x[1])];
  o.also = [];
  for (const p of extra) {
    if (o.also.length >= 4) break;
    if (p && p.available && !X[p.key] && !o.also.includes(p) && fits(used, p)) o.also.push(p);
  }

  if (s.deep === 'yes') o.notes.push('Deep, painful blemishes go further than skincare reaches. Please have someone look at them who is trained for it.');
  if (s.frag === 'unknown') o.notes.push('You are not sure whether fragrance plays a part, so try anything new on a small area for a few days first.');
  /* "On the days you are outside, sun protection belongs in the morning" (bij pigment en weinig buiten) staat sinds
     31 oktober 2026 in het vinkje van de hoofdklacht, voor iedereen met vlekken. */
  o.conditions = conditions(s, goal, touchy);
  return o;
}

/* ------------------------------------------------------------------ */
/* 3b. Nederlands                                                      */
/* ------------------------------------------------------------------ */

/* De sectielabels (L) vertaalt Shopify zelf. De teksten in dit bestand niet, daarom
   staat het Nederlands hier. Links het Engels precies zoals het in dit bestand staat,
   rechts het Nederlands. Wie een Engelse tekst aanpast, past hier de sleutel mee aan,
   anders valt die tekst in het Nederlands terug op het Engels. De test in de HQ-map
   (webshop/vertaling/skin-analysis/test.js) vangt dat af.
   Merktaal: webshop/vertaling/woordenlijst.md. Ritual, de productnamen en Step one,
   two en three blijven Engels.
   De redenen bij het vierde product komen achter het label L.because ("Omdat") en zijn
   daarom bijzinnen met het werkwoord achteraan. */

const LANG = String(document.documentElement.lang || (window.Shopify && window.Shopify.locale) || '').toLowerCase();
const IS_NL = LANG.startsWith('nl');

const NL = {
  /* Vraag 1 en 2: de klachten */
  'What is your top skin concern?': 'Wat houdt je het meest bezig aan je huid?',
  'Do you have other concerns?': 'Speelt er nog iets anders?',
  'Dark spots and marks': 'Pigmentvlekken en verkleuringen',
  'Fine lines and wrinkles': 'Fijne lijntjes en rimpels',
  'Loss of firmness': 'Een minder stevige huid',
  'Redness and sensitivity': 'Roodheid en gevoeligheid',
  'A tight, damaged feeling': 'Een trekkerig, beschadigd gevoel',
  'Dryness or dehydration': 'Een droge of vochtarme huid',
  'Dullness': 'Een vale huid',
  'Rough or uneven texture': 'Een ruwe of oneffen huidstructuur',
  'Blemishes': 'Onzuiverheden',
  'Blackheads': 'Mee-eters',
  'Visible pores': 'Zichtbare poriën',
  'Shiny areas': 'Glimmende zones',
  'Eye bags and dark circles': 'Wallen en donkere kringen',
  'I do not have any particular concern': 'Niets in het bijzonder',
  'I do not have other concerns': 'Nee, verder niets',
  'This tells us which ritual to build. Everything after it makes that ritual yours.':
    'Hiermee bepalen we welk ritual we voor je samenstellen. Alles wat daarna komt, maakt dat ritual van jou.',
  'Second concerns decide the fourth product and the swaps inside your ritual.':
    'Wat er verder speelt, bepaalt het vierde product en wat we binnen je ritual omwisselen.',

  /* Vraag 3a tot en met 3i: waar het vandaan komt */
  'Where do your dark spots and marks come from?': 'Waar komen je pigmentvlekken en verkleuringen vandaan?',
  'Marks from the sun ask for something different than marks left behind by a blemish.':
    'Vlekken door de zon vragen om iets anders dan vlekjes die achterblijven na een onzuiverheid.',
  'Sun exposure over the years': 'Veel zon, door de jaren heen',
  'They stay behind after a blemish': 'Ze blijven achter na een onzuiverheid',
  'My tone is uneven all over, without clear spots': 'Mijn teint is overal ongelijkmatig, zonder duidelijke vlekken',
  'They appeared recently': 'Ze zijn er sinds kort',
  'I do not know': 'Ik weet het niet',

  'Where do you see your fine lines and wrinkles most?': 'Waar zie je je fijne lijntjes en rimpels het meest?',
  'Where do you notice the loss of firmness most?': 'Waar merk je het meest dat je huid minder stevig is?',
  'The eye area asks for a different product than the rest of the face.':
    'De huid rond je ogen vraagt om een ander product dan de rest van je gezicht.',
  'Around the eyes': 'Rond de ogen',
  'Forehead and between the brows': 'Op het voorhoofd en tussen de wenkbrauwen',
  'Around the mouth': 'Rond de mond',
  'My face feels less firm overall': 'Mijn hele gezicht voelt minder stevig',
  'A little everywhere': 'Overal een beetje',

  'What does your skin do when the redness and sensitivity flare up?': 'Wat doet je huid als de roodheid en gevoeligheid opspelen?',
  'Rough and tight points to the barrier, warm and red points to reactivity. Those are two rituals.':
    'Ruw en trekkerig wijst op de huidbarrière, warm en rood wijst op een huid die snel reageert. Dat zijn twee verschillende rituals.',
  'It goes red and warm': 'Mijn huid wordt rood en warm',
  'It stings when I apply something': 'Het prikt als ik iets aanbreng',
  'It feels rough and tight': 'Mijn huid voelt ruw en trekkerig',
  'It flakes': 'Mijn huid schilfert',
  'Cold and wind set it off': 'Kou en wind lokken het uit',

  'What do you think set off the tight, damaged feeling?': 'Waardoor is het trekkerige, beschadigde gevoel volgens jou begonnen?',
  'If you use an acid or a retinol now, the first advice is to pause it.':
    'Gebruik je nu een zuur of een retinol, dan is het eerste advies om daar even mee te stoppen.',
  'Acids or scrubs': 'Zuren of scrubs',
  'A retinol or a plant alternative': 'Een retinol of een plantaardig alternatief',
  'Cold, wind or indoor heating': 'Kou, wind of de verwarming binnen',
  'A new product': 'Een nieuw product',
  'I cannot tell': 'Ik zou het niet weten',

  'How does your dryness or dehydration show?': 'Waaraan merk je dat je huid droog of vochtarm is?',
  'Tight without flaking is a shortage of water. Flaking is a shortage of oil.':
    'Een trekkerige huid zonder schilfers komt vocht tekort. Een huid die schilfert komt vet tekort.',
  'Tight right after cleansing': 'Trekkerig, meteen na het reinigen',
  'Flaky patches': 'Schilferige plekjes',
  'Make-up clings to dry spots': 'Make-up blijft hangen op droge plekjes',
  'It looks dull with it': 'Mijn huid ziet er ook vaal uit',
  'Only in winter': 'Alleen in de winter',


  'Tell us more about your skin.': 'Vertel ons iets meer over je huid.',
  'Tell us more about your blemishes.': 'Vertel ons iets meer over je onzuiverheden.',
  'Tell us more about your blackheads.': 'Vertel ons iets meer over je mee-eters.',
  'Tell us more about your visible pores.': 'Vertel ons iets meer over je zichtbare poriën.',
  'Tell us more about your shiny areas.': 'Vertel ons iets meer over je glimmende zones.',
  'Mostly on the chin and jawline': 'Vooral op de kin en de kaaklijn',
  'Mostly forehead and nose': 'Vooral op het voorhoofd en de neus',
  'Blackheads are the main thing': 'Het gaat vooral om mee-eters',
  'My skin gets shiny during the day': 'Mijn huid gaat in de loop van de dag glimmen',
  'My skin is dry and blemish prone at the same time': 'Mijn huid is droog en krijgt toch snel onzuiverheden',

  'What bothers you most around the eyes?': 'Wat stoort je het meest rond je ogen?',
  'Circles and puffiness ask for a different product than fine lines.':
    'Kringen en wallen vragen om een ander product dan fijne lijntjes.',
  'Dark circles': 'Donkere kringen',
  'Puffiness and bags': 'Opgezette ogen en wallen',
  'Fine lines': 'Fijne lijntjes',
  'Dryness': 'Een droge huid',
  'All of it': 'Alles',


  /* Vraag 4 tot en met 7: je huid */






  /* Vraag 8 tot en met 11: je voorkeuren */
  'I would like a leave-on acid': 'Ik wil graag een zuur dat je niet afspoelt',
  'I would rather scrub': 'Ik scrub liever',
  'Either is fine, you choose': 'Allebei prima, kiezen jullie maar',
  'I tried acids and they were too much': 'Ik heb zuren geprobeerd en dat was te veel voor mijn huid',
  'I would rather not exfoliate at all': 'Ik exfolieer liever helemaal niet',

  'What should your last step feel like?': 'Hoe moet je laatste stap aanvoelen?',
  'A light cream': 'Een lichte crème',
  'A facial oil': 'Een gezichtsolie',
  'No preference': 'Geen voorkeur',



  /* Vraag 12 tot en met 16: over jou */
  'I would rather not say': 'Zeg ik liever niet',

  'Are you pregnant or breastfeeding?': 'Ben je zwanger of geef je borstvoeding?',
  'Yes': 'Ja',
  'No': 'Nee',

  'I already wear sun protection every day': 'Ik gebruik al elke dag zonbescherming',



  /* De uitslag: kop */
  'Dark spots and an uneven tone': 'Pigmentvlekken en een ongelijkmatige teint',
  'Fine lines and firmness': 'Fijne lijntjes en stevigheid',
  'Skin that reacts easily': 'Een huid die snel reageert',
  'Skin that feels tight and worn': 'Een huid die trekkerig en uitgeput aanvoelt',
  'Dryness and dehydration': 'Een droge en vochtarme huid',
  'Dullness and texture': 'Een vale, oneffen huid',
  'Blemishes and congestion': 'Onzuiverheden en verstopte poriën',
  'The eye area': 'De huid rond je ogen',
  'A calm everyday ritual': 'Een rustig ritual voor elke dag',
  'Fragrance free, and kept short': 'Parfumvrij, en kort gehouden',

  /* De uitslag: het plan */
  'Dark spots take time. We work on them in the evening with Alpha-Arbutin and Bidens Pilosa, and keep the morning simple so the work is not undone.':
    'Pigmentvlekken vragen tijd. We werken eraan in de avond, met Alpha-Arbutin en Bidens Pilosa, en houden de ochtend eenvoudig zodat dat werk niet verloren gaat.',
  "Lines soften at the skin's own pace. A renewal active in the evening, a cream that holds moisture overnight, and nothing that works against it.":
    "Lijntjes worden zachter in het tempo van je huid. Een actieve stof voor huidvernieuwing in de avond, een crème die 's nachts vocht vasthoudt, en niets wat daartegenin werkt.",
  'Less is the whole plan. Cleanse gently, hydrate well, and leave out anything that gives your skin a reason to react.':
    'Minder is hier het hele plan. Mild reinigen, goed hydrateren, en alles weglaten wat je huid een reden geeft om te reageren.',
  'Rest first. No acids, no scrubs, no renewal actives. Moisture, fats and time, until your skin feels like itself again.':
    'Eerst rust. Geen zuren, geen scrubs, geen actieve stoffen voor huidvernieuwing. Vocht, vetten en tijd, tot je huid weer als zichzelf aanvoelt.',
  'Water first, oil on top. A hydrating layer and a last step that holds it there.':
    'Eerst vocht, dan olie eroverheen. Een hydraterende laag en een laatste stap die het vocht vasthoudt.',
  'Dullness usually sits at the surface. Clear the surface, then feed it. That is what the steps below do.':
    'Dat vale zit meestal aan de oppervlakte. Eerst de oppervlakte vrijmaken, daarna voeden. Dat is wat de stappen hieronder doen.',
  'Keep the pores clear without drying the rest out. Salicylic acid in the evening, light hydration, and a mask once or twice a week.':
    'De poriën vrijhouden zonder de rest uit te drogen. Salicylzuur in de avond, lichte hydratatie, en één of twee keer per week een masker.',
  'The skin around the eyes is thinner and shows it first. A product made for that zone, and a ritual that does not fight it.':
    'De huid rond je ogen is dunner en laat het als eerste zien. Een product dat voor die zone is gemaakt, en een ritual dat er niet tegenin werkt.',
  'No particular concern is a good place to start. Three steps, nothing loud, and room to add something later.':
    'Niets in het bijzonder is een mooi beginpunt. Drie stappen, niets heftigs, en ruimte om er later iets aan toe te voegen.',
  'Two products, and that is on purpose. These are the only two formulas in our range without any fragrance. Cleanse gently, hydrate well, and leave the rest alone until your skin feels settled.':
    'Twee producten, en dat is bewust. Dit zijn de enige twee formules in ons assortiment zonder parfum. Mild reinigen, goed hydrateren, en de rest laten liggen tot je huid weer rustig aanvoelt.',

  /* De uitslag: huidtype */
  'Dry': 'Droog',
  'Normal': 'Normaal',
  'Combination': 'Gecombineerd',
  'Oily': 'Vet',
  'It stays tight after cleansing, it hardly ever shines, and the pores are small.':
    'Je huid blijft trekkerig na het reinigen, glimt bijna nooit en heeft kleine poriën.',
  'Comfortable after cleansing, no shine to speak of, pores you have to look for.':
    'Comfortabel na het reinigen, nauwelijks glans, en poriën waar je naar moet zoeken.',
  'Shine across the nose and forehead, comfortable on the cheeks.': 'Glans op de neus en het voorhoofd, comfortabel op de wangen.',
  'Shine across the whole face by the end of the day, and pores you can see.':
    'Aan het eind van de dag glans over het hele gezicht, en poriën die je kunt zien.',

  /* De uitslag: wat we nu ook zien. Komt achter "Op dit moment zien we ook". */
  'dehydrated': 'een tekort aan vocht',
  'easily upset': 'een huid die snel van slag is',
  'fragrance intolerant': 'gevoeligheid voor parfum',
  'a barrier that needs rest': 'een huidbarrière die rust nodig heeft',
  'dull at the surface': 'een vaal huidoppervlak',
  'congested': 'verstopte poriën',
  'uneven in tone': 'een ongelijkmatige teint',
  'Your type stays roughly the same over the years. The rest changes with the season, your habits and your life, and that is the part a ritual can work on.':
    'Je huidtype blijft door de jaren heen ongeveer hetzelfde. De rest verandert mee met het seizoen, je gewoontes en je leven, en dat is het deel waar een ritual aan kan werken.',

  /* De uitslag: labels */
  'Fragrance free': 'Parfumvrij',
  'Pregnancy': 'Zwangerschap',
  'Gentle route': 'Milde route',
  'Nut free': 'Notenvrij',
  'Vegan': 'Vegan',
  'Gluten free': 'Glutenvrij',

  /* De uitslag: opmerkingen */
  'We left out the acids, the retinol alternatives and the pigment actives. Talk to your doctor or midwife before you start anything new.':
    'We hebben de zuren, de alternatieven voor retinol en de actieve stoffen voor pigmentvlekken weggelaten. Overleg met je arts of verloskundige voordat je met iets nieuws begint.',
  'If you are pregnant or breastfeeding, talk to your doctor or midwife before you start anything new.':
    'Ben je zwanger of geef je borstvoeding, overleg dan met je arts of verloskundige voordat je met iets nieuws begint.',
  'Your skin reacts easily, so no acids, no scrub, and never two renewal actives at once.':
    'Je huid reageert snel, dus geen zuren, geen scrub, en nooit twee actieve stoffen voor huidvernieuwing tegelijk.',
  'Give your skin a rest first. No exfoliant while it feels like this.':
    'Gun je huid eerst rust. Geen exfoliant zolang het zo aanvoelt.',
  'You are just starting out, so step two is a serum. The exfoliant can join once the rest is in place.':
    'Je begint net, dus step two is een serum. De exfoliant kan erbij komen zodra de basis staat.',
  'Deep, painful blemishes go further than skincare reaches. Please have someone look at them who is trained for it.':
    'Diepe, pijnlijke onzuiverheden gaan verder dan huidverzorging kan reiken. Laat er iemand naar kijken die daarvoor is opgeleid.',
  'You are not sure whether fragrance plays a part, so try anything new on a small area for a few days first.':
    'Je weet niet zeker of parfum een rol speelt, dus test alles wat nieuw is eerst een paar dagen op een klein stukje huid.',
  'On the days you are outside, sun protection belongs in the morning, otherwise the marks come back.':
    'Op de dagen dat je buiten bent, hoort zonbescherming bij je ochtend, anders komen de vlekken terug.',

  /* De uitslag: de reden bij het vierde product. Komt achter "Omdat". */
  'you asked about the eye area': 'je iets wilt doen voor de huid rond je ogen',
  'after the eye serum': 'deze na het oogserum komt',
  'the second step for the eye area': 'dit de tweede stap is voor de huid rond je ogen',
  'you are in the sun now and then': 'je af en toe in de zon bent',
  'you are outdoors most days': 'je de meeste dagen buiten bent',
  'you wear make-up or sun protection every day': 'je elke dag make-up of zonbescherming draagt',
  'for blackheads and pores': 'dit er is voor mee-eters en poriën',
  'the morning partner to your evening serum': 'dit in de ochtend aansluit op je serum van de avond',
  'so day and night each have their own cream': 'dag en nacht zo elk een eigen crème krijgen',
  'a fragrance free layer of water after cleansing': 'dit na het reinigen een parfumvrij laagje vocht geeft',
  'fats on top, or water underneath': 'je huid nu vraagt om vetten bovenop, of vocht eronder',
  'water first, oil on top': 'eerst vocht komt, en dan pas olie',
  'antioxidants on top of your three steps': 'dit antioxidanten toevoegt aan je drie stappen',
  'the one product that works on blackheads': 'dit het enige product is dat op mee-eters werkt',
  'a quiet layer of water under your cream': 'dit een rustig laagje vocht onder je crème legt',
  'a layer of water after cleansing': 'dit na het reinigen een laagje vocht geeft',
  'a cream of its own for the night': 'de nacht zo een eigen crème krijgt',
  'a cream of its own for the morning': 'de ochtend zo een eigen crème krijgt',

  /* De uitslag: de stappen. Blijven Engels, zoals op het etiket. */
  'Step one': 'Step one',
  'Step two': 'Step two',
  'Step three': 'Step three',

  /* Reserve voor als een sectielabel leeg is */
  'Skin analysis': 'Huidanalyse',
  'Close': 'Sluiten',
  'Previous': 'Vorige',
  'Next': 'Volgende',
  'See my results': 'Bekijk mijn resultaat',
  'Add': 'Toevoegen',
  'Added': 'Toegevoegd',
  'View cart': 'Bekijk winkelmand',
  'Products': 'Producten',
  'Add ritual to cart': 'Ritual in winkelmand',
  'And': 'En',
  'Because': 'Omdat',
  'Email': 'E-mailadres',
  'Email my results': 'Mail mijn resultaat',
  'privacy policy': 'privacybeleid',
  'Start again': 'Opnieuw beginnen',
  /* De vinkjes onder Your skin (31 oktober 2026) */
  "Your skin makes less oil of its own, so it holds on to water less easily. That is why it feels tight after cleansing and hardly ever shines. Cleanse with lukewarm water rather than hot, and put your cream on while your skin is still slightly damp, so it keeps that water in.":
    "Je huid maakt zelf minder talg aan en houdt daardoor minder goed vocht vast. Daarom voelt hij trekkerig na het reinigen en glimt hij bijna nooit. Reinig met lauw water in plaats van warm, en breng je crème aan terwijl je huid nog licht vochtig is, zodat dat vocht erin blijft.",
  "Your skin is in balance: enough oil to stay supple, not so much that it shines. The aim is to keep it that way. A mild cleanser, moisture morning and evening, and one new product at a time, so you always know what your skin likes.":
    "Je huid is in balans: genoeg talg om soepel te blijven, niet zoveel dat hij glimt. Het doel is om dat zo te houden. Een milde reiniger, 's ochtends en 's avonds vocht, en één nieuw product tegelijk, zodat je altijd weet wat je huid fijn vindt.",
  "Your forehead, nose and chin make more oil than your cheeks, so one part of your face asks for less and the other for more. Cleanse gently, so your cheeks stay comfortable, and use a little less cream on the shiny areas than on the rest.":
    "Je voorhoofd, neus en kin maken meer talg aan dan je wangen. Het ene deel van je gezicht vraagt dus om minder, het andere om meer. Reinig zacht, zodat je wangen comfortabel blijven, en gebruik op de glimmende plekken iets minder crème dan op de rest.",
  "Your skin makes more oil than it needs, which shows as shine during the day and pores you can see. It still needs moisture, just in a lighter texture. Cleanse morning and evening without scrubbing hard, and leave out anything that makes your skin feel stripped.":
    "Je huid maakt meer talg aan dan hij nodig heeft. Dat zie je aan glans in de loop van de dag en aan zichtbare poriën. Ook een vette huid heeft vocht nodig, alleen in een lichtere textuur. Reinig 's ochtends en 's avonds zonder hard te boenen, en laat alles weg waardoor je huid strak en uitgedroogd aanvoelt.",
  "You were not sure, so we take the gentle route of a dry skin.":
    "Je wist het niet zeker, dus we kiezen de zachte route van een droge huid.",
  "dark spots and marks":
    "pigmentvlekken en verkleuringen",
  "fine lines and wrinkles":
    "fijne lijntjes en rimpels",
  "loss of firmness":
    "een minder stevige huid",
  "redness and sensitivity":
    "roodheid en gevoeligheid",
  "a tight, worn feeling":
    "een trekkerige, schrale huid",
  "dryness and dehydration":
    "een droge of vochtarme huid",
  "dullness":
    "een vale huid",
  "rough or uneven texture":
    "een ruwe of oneffen huidstructuur",
  "blemishes":
    "onzuiverheden",
  "blackheads":
    "mee-eters",
  "visible pores":
    "zichtbare poriën",
  "shiny areas":
    "glimmende zones",
  "eye bags and dark circles":
    "wallen en donkere kringen",
  "No particular concern.":
    "Niets in het bijzonder.",
  "They are places where your skin made extra pigment, after sun or after a blemish, and they fade slowly, layer by layer. The serum in step two is the one that works on them, so use it every day. Sun protection in the morning matters just as much, because daylight darkens them again. Think in months rather than weeks.":
    "Het zijn plekken waar je huid extra pigment heeft aangemaakt, na zon of na een onzuiverheid. Ze vervagen langzaam, laagje voor laagje. Het serum van step two werkt eraan, dus gebruik het elke dag. Zonbescherming in de ochtend is net zo belangrijk, want daglicht maakt ze weer donkerder. Reken op maanden, niet op weken.",
  "They are places where your skin made extra pigment, after sun or after a blemish, and they fade slowly, layer by layer. Sun protection in the morning counts most here, because daylight darkens them again. Think in months rather than weeks.":
    "Het zijn plekken waar je huid extra pigment heeft aangemaakt, na zon of na een onzuiverheid. Ze vervagen langzaam, laagje voor laagje. Hier telt vooral zonbescherming in de ochtend, want daglicht maakt ze weer donkerder. Reken op maanden, niet op weken.",
  "Lines show first where your skin moves most and where it is driest, around the eyes and the mouth. They soften with regular use, not with more product. Use your serum every day, keep your skin moist morning and evening, and wear sun protection on the days you are outside, so you keep what you build up.":
    "Lijntjes verschijnen het eerst waar je huid het meest beweegt en waar hij het droogst is: rond je ogen en je mond. Ze worden zachter bij regelmatig gebruik, niet door meer product. Gebruik je serum elke dag, houd je huid 's ochtends en 's avonds vochtig en draag zonbescherming op de dagen dat je buiten bent, zodat je behoudt wat je opbouwt.",
  "Lines show first where your skin moves most and where it is driest, around the eyes and the mouth. Keep your skin moist morning and evening, and wear sun protection on the days you are outside, so you keep what you build up.":
    "Lijntjes verschijnen het eerst waar je huid het meest beweegt en waar hij het droogst is: rond je ogen en je mond. Houd je huid 's ochtends en 's avonds vochtig en draag zonbescherming op de dagen dat je buiten bent, zodat je behoudt wat je opbouwt.",
  "Firmness is how springy your skin feels. Skin that holds less moisture feels less firm, often along the jaw and the cheeks. Keep it supple with your serum every day and a cream that holds the moisture in, and protect it from the sun on the days you are outside.":
    "Stevigheid is hoe veerkrachtig je huid aanvoelt. Een huid die minder vocht vasthoudt, voelt minder stevig, vaak langs je kaaklijn en je wangen. Houd hem soepel met elke dag je serum en een crème die het vocht vasthoudt, en bescherm hem tegen de zon op de dagen dat je buiten bent.",
  "Firmness is how springy your skin feels. Skin that holds less moisture feels less firm, often along the jaw and the cheeks. Keep it supple with moisture morning and evening, and protect it from the sun on the days you are outside.":
    "Stevigheid is hoe veerkrachtig je huid aanvoelt. Een huid die minder vocht vasthoudt, voelt minder stevig, vaak langs je kaaklijn en je wangen. Houd hem soepel met 's ochtends en 's avonds vocht, en bescherm hem tegen de zon op de dagen dat je buiten bent.",
  "Your skin turns red or stings quickly, from heat, cold, a new product or rubbing. The fewer reasons you give it, the calmer it stays. Use few products, lukewarm water and a soft towel to pat dry, and add one new product at a time, on a small area first.":
    "Je huid wordt snel rood of gaat prikken, door warmte, kou, een nieuw product of wrijven. Hoe minder aanleiding je hem geeft, hoe rustiger hij blijft. Gebruik weinig producten, lauw water en een zachte handdoek om je huid droog te deppen, en probeer één nieuw product tegelijk, eerst op een klein stukje huid.",
  "Skin that feels tight, rough or stings after cleansing is asking for rest. Acids, scrubs and strong cleansers take more from it than it can make up for. Leave them out for now, cleanse mildly, and give your skin moisture and fats morning and evening until it feels like itself again.":
    "Een huid die na het reinigen trekt, ruw aanvoelt of prikt, vraagt om rust. Zuren, scrubs en sterke reinigers nemen er meer van weg dan hij kan aanvullen. Laat ze voorlopig weg, reinig mild en geef je huid 's ochtends en 's avonds vocht en vet, tot hij weer als zichzelf aanvoelt.",
  "Dry skin is short of oil, dehydrated skin is short of water, and many people have both. You feel it as tightness, flaky patches or make-up that clings. Apply your products on slightly damp skin and finish with your cream, which keeps the water in. Lukewarm water instead of hot helps too.":
    "Een droge huid heeft te weinig talg, een vochtarme huid te weinig water, en veel mensen hebben allebei. Je merkt het aan een trekkerig gevoel, schilferige plekjes of make-up die blijft hangen. Breng je producten aan op een licht vochtige huid en eindig met je crème, die het water vasthoudt. Lauw water in plaats van warm helpt ook.",
  "Skin looks dull when its surface is uneven or a little dry, so it reflects less light. The answer sits on that surface: clear it gently and keep it moist, so it smooths out. If you exfoliate, a few times a week is enough, and always follow with moisture.":
    "Een huid ziet er vaal uit als het oppervlak oneffen of een beetje droog is: dan weerkaatst hij minder licht. Daar zit ook de oplossing. Maak het oppervlak zachtjes glad en houd het vochtig. Als je exfolieert, is een paar keer per week genoeg, en geef je huid daarna altijd vocht.",
  "Texture is what you feel under your fingertips: small bumps, rough patches, a surface that is not smooth. It sits in the top layer of your skin, so gentle care works best here. If you exfoliate, a few times a week is enough, and moisture every day keeps the surface soft. Pressing harder does not work faster.":
    "Huidstructuur is wat je onder je vingertoppen voelt: kleine bultjes, ruwe plekjes, een oppervlak dat niet glad is. Het zit in de bovenste laag van je huid, dus zachte verzorging werkt hier het best. Als je exfolieert, is een paar keer per week genoeg, en elke dag vocht houdt het oppervlak zacht. Harder drukken werkt niet sneller.",
  "A blemish starts in a pore that is blocked by oil and flakes of skin. The work is keeping pores clear without drying out the rest of your face. Cleanse morning and evening, change your pillowcase often, and leave blemishes alone: squeezing leaves marks that stay longer than the blemish itself.":
    "Een onzuiverheid begint in een porie die verstopt raakt door talg en huidschilfers. Het gaat erom je poriën vrij te houden zonder de rest van je gezicht uit te drogen. Reinig 's ochtends en 's avonds, verschoon je kussensloop vaak en blijf van onzuiverheden af: uitknijpen laat plekjes achter die langer blijven dan de onzuiverheid zelf.",
  "A blackhead is a pore filled with oil that turns dark where it meets the air, mostly on the nose and chin. Keeping those pores clear every day is what helps. Cleanse well in the evening, especially after make-up or sun protection, and leave squeezing alone: it irritates the skin around the pore.":
    "Een mee-eter is een porie vol talg die donker kleurt waar hij met lucht in aanraking komt, meestal op je neus en kin. Wat helpt, is die poriën elke dag vrijhouden. Reinig 's avonds goed, zeker na make-up of zonbescherming, en knijp ze niet uit: dat irriteert de huid rond de porie.",
  "Pores are part of your skin, and how big they are is mostly a given. They stand out more when they are full of oil or when the skin around them is dry. Keep them clear with a thorough cleanse in the evening, keep your skin moist, and use lighter textures where you see them most.":
    "Poriën horen bij je huid, en hoe groot ze zijn, ligt grotendeels vast. Ze vallen meer op als ze vol talg zitten of als de huid eromheen droog is. Houd ze vrij met 's avonds een grondige reiniging, houd je huid vochtig en gebruik lichtere texturen op de plekken waar je ze het meest ziet.",
  "Shine is oil on the surface, mostly on the forehead, nose and chin. Your skin needs some of that oil, so the aim is balance, not drying it out. Cleanse gently morning and evening, choose light textures, and blot during the day instead of washing more often.":
    "Glans is talg op het oppervlak, meestal op je voorhoofd, neus en kin. Je huid heeft een deel van die talg nodig, dus het doel is balans, niet uitdrogen. Reinig 's ochtends en 's avonds zacht, kies lichte texturen en dep overdag de glans weg in plaats van vaker te wassen.",
  "The skin around your eyes is thinner than elsewhere on your face, so tiredness, fluid and colour show there first. Puffiness is mostly fluid that settles overnight, and dark circles often run in the family. Use a product made for this zone, tap it in with your ring finger instead of rubbing, and wear sun protection around the eyes too.":
    "De huid rond je ogen is dunner dan elders op je gezicht, dus vermoeidheid, vocht en kleur zie je daar het eerst. Wallen zijn meestal vocht dat zich 's nachts ophoopt, en donkere kringen zitten vaak in de familie. Gebruik een product dat voor deze zone is gemaakt, tik het in met je ringvinger in plaats van te wrijven, en draag ook rond je ogen zonbescherming.",
  "Then the aim is to keep your skin as it is: clean, moist and protected. Three steps are enough for that. Add something only when your skin asks for it, and one product at a time, so you know what it does.":
    "Dan is het doel je huid te houden zoals hij is: schoon, vochtig en beschermd. Drie stappen zijn daar genoeg voor. Voeg alleen iets toe als je huid erom vraagt, en één product tegelijk, zodat je weet wat het doet.",
  "Short on water right now.":
    "Nu even vochtarm.",
  "Any skin type can be, oily skin too: it feels tight even when it shines. Apply your products on slightly damp skin and finish with your cream, which keeps the water in.":
    "Dat kan bij elk huidtype, ook bij een vette huid: hij voelt trekkerig, ook als hij glimt. Breng je producten aan op een licht vochtige huid en eindig met je crème, die het water vasthoudt.",
  "Quick to react.":
    "Reageert snel.",
  "That is why your ritual has no acids, no scrub and never two renewal actives at once. Keep the water lukewarm, pat dry instead of rubbing, and try anything new on a small area for a few days before it goes on your whole face.":
    "Daarom zitten er in je ritual geen zuren, geen scrub en nooit twee actieve stoffen voor huidvernieuwing tegelijk. Gebruik lauw water, dep je huid droog in plaats van te wrijven, en probeer iets nieuws eerst een paar dagen op een klein stukje huid voordat het op je hele gezicht gaat.",
  "Better without fragrance.":
    "Beter zonder parfum.",
  "Everything in your ritual is without it. Keep it that way with the rest of what you put on your face.":
    "Alles in je ritual is zonder parfum. Houd dat zo bij de rest van wat je op je gezicht aanbrengt.",
  "In need of rest.":
    "Toe aan rust.",
  "Rough and tight means your skin is losing more moisture than it holds. Leave out acids, scrubs and renewal actives for now, and give it moisture and fats morning and evening.":
    "Ruw en trekkerig betekent dat je huid meer vocht verliest dan hij vasthoudt. Laat zuren, scrubs en actieve stoffen voor huidvernieuwing voorlopig weg, en geef je huid 's ochtends en 's avonds vocht en vet.",
  "Dull at the surface.":
    "Vaal aan het oppervlak.",
  "That sits in the top layer of your skin. Moisture every day keeps the surface smooth, and if you exfoliate, a few times a week is enough.":
    "Dat zit in de bovenste laag van je huid. Elke dag vocht houdt het oppervlak glad, en als je exfolieert, is een paar keer per week genoeg.",
  "Congested in places.":
    "Hier en daar verstopte poriën.",
  "Oil collects in the pores, mostly on the forehead, nose and chin. Cleanse thoroughly in the evening, especially after make-up or sun protection, and keep heavy textures away from those areas.":
    "Talg hoopt zich op in de poriën, meestal op je voorhoofd, neus en kin. Reinig 's avonds grondig, zeker na make-up of zonbescherming, en houd zware texturen weg van die plekken.",
  "Uneven in tone.":
    "Een ongelijkmatige teint.",
  "Spots and patches darken with daylight, so sun protection in the morning helps to keep them from getting darker on the days you are outside.":
    "Vlekken en plekjes worden donkerder door daglicht. Zonbescherming in de ochtend helpt om ze niet donkerder te laten worden op de dagen dat je buiten bent.",
  /* How to use this ritual (31 oktober 2026) */
  'Every morning': 'Elke ochtend',
  'Every evening': 'Elke avond',
  'Not every day': 'Niet elke dag',
  'How to apply': 'Zo breng je het aan',
  'your evening needs a cream of its own': 'je avond ook een eigen crème nodig heeft',
  'your morning needs a cream of its own': 'je ochtend ook een eigen crème nodig heeft',
  'Work a small amount over a damp face in gentle circles, then rinse with lukewarm water and pat dry.':
    'Masseer een klein beetje met zachte cirkels over je vochtige gezicht, spoel af met lauw water en dep je huid droog.',
  'Massage one or two pumps onto dry or damp skin in gentle circles, then rinse with lukewarm water and pat dry.':
    'Masseer één of twee pompjes met zachte cirkels op een droge of vochtige huid, spoel af met lauw water en dep je huid droog.',
  'Massage one pump onto dry skin to dissolve make-up, add a splash of water so the oil turns into a milk, then rinse with lukewarm water and pat dry.':
    'Masseer één pompje op een droge huid om make-up op te lossen, voeg een beetje water toe zodat de olie een melk wordt, spoel af met lauw water en dep je huid droog.',
  'Apply with a cotton pad or your hands, with extra attention for the T-zone, and let it dry. Do not rinse.':
    'Breng aan met een wattenschijfje of je handen, met extra aandacht voor de T-zone, en laat het drogen. Niet afspoelen.',
  'Apply with a cotton pad or press it in with clean hands, and let it absorb. Do not rinse.':
    'Breng aan met een wattenschijfje of druk het in met schone handen, en laat het intrekken. Niet afspoelen.',
  'Massage onto damp skin in gentle circles for 30 to 60 seconds, away from the eyes, then rinse with lukewarm water and pat dry.':
    'Masseer 30 tot 60 seconden met zachte cirkels op een vochtige huid, niet rond je ogen, spoel af met lauw water en dep je huid droog.',
  'Press a few drops into clean, dry skin, away from the eyes, and leave it on overnight. A light tingle is normal.':
    'Druk een paar druppels in een schone, droge huid, niet rond je ogen, en laat het een nacht intrekken. Een licht tintelend gevoel is normaal.',
  'Press one pump into clean skin and wait a minute before the next step.':
    'Druk één pompje in een schone huid en wacht een minuut voor de volgende stap.',
  'Press a few drops into clean skin and wait a minute before the next step.':
    'Druk een paar druppels in een schone huid en wacht een minuut voor de volgende stap.',
  'Press a few drops into clean skin.': 'Druk een paar druppels in een schone huid.',
  'Tap a small amount around the eyes with your ring finger, without touching the eyes themselves, and let it absorb.':
    'Tik een klein beetje rond je ogen met je ringvinger, zonder je ogen zelf te raken, en laat het intrekken.',
  'Tap a small amount around the eyes with your ring finger, from the inner corner outward, without touching the eyes themselves, and let it absorb.':
    'Tik een klein beetje rond je ogen met je ringvinger, van de binnenhoek naar buiten, zonder je ogen zelf te raken, en laat het intrekken.',
  'Warm one pump between your fingertips and press it onto your skin. Let it absorb before make-up.':
    'Warm één pompje op tussen je vingertoppen en druk het op je huid. Laat het intrekken voor je make-up aanbrengt.',
  'Warm one pump between your fingertips and press it onto your skin.':
    'Warm één pompje op tussen je vingertoppen en druk het op je huid.',
  'Warm three to four drops between your palms and press them onto your skin.':
    'Warm drie tot vier druppels op tussen je handpalmen en druk ze op je huid.',
  'Apply generously and blend with your fingertips, over make-up too. Reapply at least every two hours, and after swimming, sweating or towel drying.':
    'Breng ruim aan en werk het uit met je vingertoppen, ook over make-up. Breng minstens elke twee uur opnieuw aan, en na zwemmen, zweten of afdrogen.',
  'Spread a generous amount evenly over your skin as the last step of the morning. Reapply after swimming, sweating or towel drying.':
    'Verdeel een ruime hoeveelheid gelijkmatig over je huid, als laatste stap van de ochtend. Breng opnieuw aan na zwemmen, zweten of afdrogen.',
  'Apply a generous layer to clean, dry skin, away from the eyes. Leave it on for 5 to 10 minutes, then rinse with lukewarm water and pat dry.':
    'Breng een ruime laag aan op een schone, droge huid, niet rond je ogen. Laat het 5 tot 10 minuten zitten, spoel af met lauw water en dep je huid droog.',
  'Mix 1 to 3 drops into your serum or cream in the palm of your hand, then apply as usual.':
    'Meng 1 tot 3 druppels met je serum of crème in je handpalm en breng het aan zoals altijd.',
  'Two or three times a week, morning or evening, never two days in a row.':
    "Twee of drie keer per week, 's ochtends of 's avonds, nooit twee dagen achter elkaar.",
  'One or two evenings a week, never two evenings in a row, and wear sun protection the next morning.':
    'Eén of twee avonden per week, nooit twee avonden achter elkaar, en de ochtend erna zonbescherming.',
  'Once or twice a week, morning or evening, after cleansing.':
    "Eén of twee keer per week, 's ochtends of 's avonds, na het reinigen.",
  /* De regels van de collecties en het uitklappen van de aanvulling (5 oktober 2026, avond) */
  'You wear make-up or sun protection every day, so you cleanse with the oil cleanser: it takes everything off in one go.':
    'Je draagt elke dag make-up of zonbescherming, dus je reinigt met de oliereiniger: die haalt alles in één keer weg.',
  'the salicylic acid toner works on your blemishes, after cleansing': 'de toner met salicylzuur je onzuiverheden aanpakt, na het reinigen',
  'Also a good addition': 'Ook een goede aanvulling',
  'Add to ritual': 'Aan ritual toevoegen',
  'Remove': 'Verwijderen',
  /* Ontwerp C (30 oktober 2026): het moment achter de stap en de nieuwe kopjes. */
  'Morning and evening': 'Ochtend en avond',
  'Morning': 'Ochtend',
  'Evening': 'Avond',
  'Your skin': 'Je huid',
  'All products that suit you': 'Alle producten die bij je passen',
  'Chosen for your skin': 'Gekozen voor jouw huid',
  'Add ritual': 'Ritual toevoegen',
  'Add to bag': 'In winkelmand',
  'Treatment': 'Treatment',
  'How to use this ritual': 'Zo gebruik je dit ritual',
  'Save [percent]%': 'Bespaar [percent]%',
  'By sending, you also sign up for our email list.': 'Door te versturen meld je je ook aan voor onze e-mails.',
  'Send': 'Versturen',
  'Your results are on their way to your inbox.': 'Je resultaat is onderweg naar je inbox.',
  'Your ritual is complete with six products.': 'Je ritual is compleet met zes producten.',
  'Add products to your full ritual. The ritual discount counts for them too.':
    'Voeg producten toe aan je complete ritual. De ritual discount geldt ook voor wat je toevoegt.',
  /* De korte versie (5 oktober 2026) */
  'Fine lines around the eyes':
    'Fijne lijntjes rond de ogen',
  'Blackheads ask for the mask. The odd blemish asks for the toner. Deep, painful blemishes go further than skincare reaches, and we would rather say so.':
    'Mee-eters vragen om het masker. Een onzuiverheid af en toe vraagt om de toner. Diepe, pijnlijke onzuiverheden gaan verder dan huidverzorging kan reiken, en dat zeggen we liever eerlijk.',
  'Some are painful or sit deep under the skin':
    'Sommige doen pijn of zitten diep onder de huid',
  'How does your skin feel?':
    'Hoe voelt jouw huid aan?',
  'Pick the one that fits best.':
    'Kies wat het meest bij je past.',
  'This is how we read your skin type. If you are not sure, we take the gentle route of a dry skin.':
    'Zo lezen we je huidtype. Weet je het niet zeker, dan kiezen we de zachte route van een droge huid.',
  'Tight after cleansing, hardly ever shiny, small pores':
    'Trekkerig na het reinigen, glimt bijna nooit, kleine poriën',
  'Comfortable after cleansing, no real shine, pores you have to look for':
    'Comfortabel na het reinigen, glimt niet echt, poriën die je moet zoeken',
  'Shiny on the nose and forehead, comfortable on the cheeks':
    'Glimmend op de neus en het voorhoofd, comfortabel op de wangen',
  'Shiny all over by the end of the day, pores you can see':
    'Aan het eind van de dag overal glimmend, met zichtbare poriën',
  'I am just starting out, keep it simple':
    'Ik begin net, houd het eenvoudig',
  'A rich cream': 'Een rijke crème',
  'I mostly take care of my skin in the evening':
    "Ik verzorg mijn huid vooral 's avonds",
  'So we leave out ingredients you are better off not using while pregnant or breastfeeding.':
    'Zo laten we ingrediënten weg die je tijdens zwangerschap of borstvoeding beter niet gebruikt.',
  'This does not apply to me':
    'Geldt niet voor mij',
  'Indoors most days':
    'De meeste dagen binnen',
  'Some sun, in summer and on holiday':
    'Af en toe zon, in de zomer en op vakantie',
  'Outdoors most days':
    'De meeste dagen buiten',
  'I wear make-up every day':
    'Ik draag elke dag make-up',
  /* De versie met 8 vragen (5 oktober 2026) */
  'Exfoliating':
    'Exfoliëren',
  /* Vraag 5 en 8 in twee delen, en weet ik niet (5 oktober 2026) */
  'A skin that often reacts gets a gentle ritual, without acids, scrubs or two renewal actives at once. If you are not sure, we take that gentle route too. Otherwise you choose how you like to exfoliate.':
    'Een huid die vaak reageert, krijgt een zacht ritual, zonder zuren, scrub of twee actieve stoffen voor huidvernieuwing tegelijk. Weet je het niet zeker, dan nemen we ook die zachte route. Anders kies je zelf hoe je wilt exfoliëren.',
  'Does your skin often react to new products (redness, itching or stinging)?':
    'Reageert je huid vaak op nieuwe producten (roodheid, jeuk of prikken)?',
  'How do you feel about exfoliating?':
    'Hoe sta je tegenover exfoliëren?',
  'I have never exfoliated':
    'Ik heb nog nooit geëxfolieerd',
  'The last step is the one you feel all day, so you get to choose it. If you do not know yet, we choose it for your skin. Tell us if your skin care is mostly an evening moment.':
    "De laatste stap voel je de hele dag, dus die mag je zelf kiezen. Weet je het nog niet, dan kiezen wij hem bij je huid. Zeg het erbij als je je huid vooral 's avonds verzorgt.",
  'Pick one, and add the evening if it fits.':
    'Kies er één, en zet de avond erbij als dat klopt.',
  'Two last things':
    'Nog twee dingen',
  'If you are hardly ever outside, we are not going to push sun protection into your ritual. Daily make-up or sun protection asks for an oil cleanser in the evening. Almond oil is in the facial oil and in the vitamin C serum, beeswax is in the tinted sun fluid, and without fragrance you get the only two formulas without it.':
    "Kom je bijna nooit buiten, dan gaan we je geen zonbescherming opdringen in je ritual. Dagelijkse make-up of zonbescherming vraagt 's avonds om een oliereiniger. Amandelolie zit in de gezichtsolie en in het vitamine C-serum, bijenwas zit in de getinte zonnebrandcrème, en zonder parfum krijg je de enige twee formules zonder parfum.",
  'What does a normal day look like for your skin?':
    'Hoe ziet een gewone dag eruit voor je huid?',
  'Pick one. Make-up can go with it.':
    'Kies er één. Make-up mag erbij.',
  'Is there anything else we should take into account?':
    'Moeten we nog ergens rekening mee houden?',
  'Nothing':
    'Niets',
  'No fragrance':
    'Geen parfum',
  'No nuts, including almond':
    'Geen noten, ook geen amandel',
  /* Zeggen wat er nog mist als iemand op Volgende klikt (Jesse, 7 oktober 2026) */
  'Choose an answer.':
    'Kies nog een antwoord.',
  'Choose at least one answer.':
    'Kies nog minstens één antwoord.',
  'Choose at least one, or that there is nothing else.':
    'Kies er nog minstens één, of dat er verder niets is.',
  'Choose whether your skin often reacts.':
    'Kies nog of je huid vaak reageert.',
  'Choose how you feel about exfoliating.':
    'Kies nog hoe je tegenover exfoliëren staat.',
  'Choose what your last step should feel like.':
    'Kies nog hoe je laatste stap moet aanvoelen.',
  'Choose how often you are in the sun.':
    'Kies nog hoe vaak je in de zon bent.',
  'Choose whether we should take anything into account, or choose Nothing.':
    'Kies nog of we ergens rekening mee moeten houden, of kies Niets.',
};

/** Nederlands als de pagina Nederlands is en de tekst in NL staat, anders de invoer. */
/* Ook de knoppen en kopjes uit de sectie die na 20 september 2026 zijn bijgekomen (Your skin, How to use this ritual,
   Save [percent]%, enzovoort) gaan door t(): ze staan niet in het template, dus Shopify kan ze niet vertalen. Een kopje
   dat Shopify wel vertaalt, is al Nederlands en komt ongewijzigd terug (31 oktober 2026). */
function t(en) {
  return IS_NL && Object.prototype.hasOwnProperty.call(NL, en) ? NL[en] : en;
}

/* De twee zinnen die uit stukken bestaan. In het Engels letterlijk de zin van voorheen. */
function sayType(typename) {
  return IS_NL ? `Jouw huidtype: ${t(typename).toLowerCase()}. ` : `Your skin type: ${typename}. `;
}

function sayConcern(name) {
  return IS_NL ? `Wat je het meest bezighoudt: ${t(name)}.` : `Your main concern: ${name}.`;
}

/**
 * De vinkjes onder "Your skin" (31 oktober 2026), ook voor de mail: je huidtype met wat het is en hoe je ermee omgaat,
 * je hoofdklacht met wat er speelt en wat helpt, de huidcondities die er nog bij komen, en de aantekeningen.
 * @returns {[string, string][]} vet begin en tekst
 */
function skinTicks(r) {
  const type = [r.typeUnsure ? t(TYPEUNSURE) : '', t(TYPECARE[r.typekey] || r.typeline)].filter(Boolean).join(' ');
  const out = [[sayType(r.typename).trim(), type]];
  const zorg = CONCERNCARE[r.concern] || CONCERNCARE.none;
  out.push([zorg[0] ? sayConcern(zorg[0]) : t('No particular concern.'), t(!r.two && zorg[2] ? zorg[2] : zorg[1])]);
  const shown = [];
  for (const c of r.conditions || []) {
    const extra = CONDCARE[c];
    if (extra && !extra[2].includes(r.concern)) { out.push([t(extra[0]), t(extra[1])]); shown.push(c); }
  }
  /* Staat "Quick to react" er, dan zegt dat vinkje al wat de aantekening zegt. */
  for (const n of r.notes) if (!(n === TOUCHY_NOTE && shown.includes('easily upset'))) out.push(['', t(n)]);
  return out;
}

function sayCount(number, of) {
  return IS_NL ? `Vraag ${number} van ${of}` : `Question ${number} of ${of}`;
}

function sayConditions(list) {
  const c = list.map(t);
  const and = IS_NL ? ' en ' : ' and ';
  const joined = c.length === 1 ? c[0] : `${c.slice(0, -1).join(', ')}${and}${c[c.length - 1]}`;
  return IS_NL ? `Op dit moment zien we ook ${joined}. ` : `Right now it also reads as ${joined}. `;
}

/* ------------------------------------------------------------------ */
/* 4. Hulpjes                                                          */
/* ------------------------------------------------------------------ */

function money(cents) {
  const format = CFG?.money || '€{{amount_with_comma_separator}}';
  const amount = cents / 100;
  const parts = (value, decimals, thousands, decimal) => {
    const [whole, frac] = value.toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac !== undefined ? grouped + decimal + frac : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount': return parts(amount, 2, ',', '.');
      case 'amount_no_decimals': return parts(amount, 0, ',', '.');
      case 'amount_with_comma_separator': return parts(amount, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return parts(amount, 0, '.', ',');
      case 'amount_with_space_separator': return parts(amount, 2, ' ', ',');
      case 'amount_with_period_and_space_separator': return parts(amount, 2, ' ', '.');
      default: return parts(amount, 2, '.', ',');
    }
  });
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function h(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const ICON_CLOSE = '<svg viewBox="0 0 18 18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 2l14 14M16 2L2 16"/></svg>';

function cartSections() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
  return [...new Set(ids)].join(',');
}

/**
 * @param {Product[]} products
 * @param {HTMLButtonElement} button
 * @param {HTMLElement} [error]
 * @param {number} [quantity] - het aantal per product (het kaartje met de aantalkiezer)
 */
async function addToCart(products, button, error, quantity = 1) {
  const items = products.filter((p) => p && p.available).map((p) => ({ id: p.variant, quantity }));
  if (!items.length || button.getAttribute('aria-busy') === 'true') return;
  button.setAttribute('aria-busy', 'true');
  if (error) error.hidden = true;
  const label = button.textContent;
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: cartSections(), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'cart add failed');
    const cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' }).then((result) => result.json());
    document.dispatchEvent(new CartAddEvent(cart, 'au-skin-analysis', {
      source: 'au-skin-analysis',
      itemCount: items.length,
      sections: data.sections,
    }));
    track('skin_analysis_added_to_cart', {
      products: products.filter((p) => p && p.available).map((p) => p.handle), items: items.length,
      value: products.filter((p) => p && p.available).reduce((sum, p) => sum + p.price, 0) / 100,
    });
    button.textContent = L.added || t('Added');
    setTimeout(() => { button.textContent = label; }, 2200);
    const holder = button.closest('.au-sa__totals, .au-sa__card');
    if (holder && !holder.querySelector('.au-sa__viewcart')) {
      const view = h('a', `au-sa__viewcart ${S.body || 'paragraph'} au-small`, L.viewCart || t('View cart'));
      view.href = Theme.routes.cart_url;
      holder.append(view);
    }
  } catch (problem) {
    console.error('[au-skin-analysis]', problem);
    if (error) { error.textContent = L.error || String(problem); error.hidden = false; }
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/* ------------------------------------------------------------------ */
/* 5. Het venster                                                      */
/* ------------------------------------------------------------------ */

/** @type {Record<string, any>} */
let state = {};
let index = 0;
/** @type {HTMLElement | null} */
let win = null;
/** @type {Element | null} */
let returnFocus = null;

const visible = () => Q.filter((q) => !q.when || q.when(state));
/* De delen van een vraag die nu gelden (een vraag zonder delen is één deel). */
const delenVan = (q) => (q.parts ? q.parts.filter((d) => !d.when || d.when(state)) : [q]);
/* Bij een deel met "one" (zon of binnen) moet daar precies één van gekozen zijn; de rest mag erbij. Een vraag in
   delen is pas beantwoord als elk deel dat geldt beantwoord is. */
const deelAf = (d) => (d.kind === 'multi'
  ? Array.isArray(state[d.key]) && state[d.key].length > 0 && (!d.one || state[d.key].some((x) => d.one.includes(x)))
  : Boolean(state[d.key]));
const answered = (q) => delenVan(q).every(deelAf);

/**
 * Hoeveel vragen deze bezoeker krijgt, voor de teller (Jesse, 5 oktober 2026: "vraag 4 van 10", en wie er 9 krijgt
 * mag niet blijven hangen op 9 van 10). Zolang de hoofdklacht nog niet gekozen is, telt de vervolgvraag mee; daarna
 * telt alleen wat er echt komt, dus het getal klopt altijd met het aantal dat iemand nog krijgt.
 */
const total = () => visible().length + (state.top ? 0 : 1);

/* ---------- Meten (Jesse, 5 oktober 2026) ----------
 * Meldingen voor de eigen pixel in Shopify (Instellingen, Klantgebeurtenissen), via de meldingen van Shopify zelf
 * (Shopify.analytics.publish). Die pixel stuurt ze alleen door naar Meta als de klant marketingcookies accepteert.
 * Per keer dat de test opent: gestart, elke vraag één keer (met het nummer), de uitslag, en iets in het mandje vanaf
 * de uitslag. Gaat iemand terug, dan telt een vraag niet nog een keer. */
let seen = new Set();
/* De voorbeelduitslag telt nergens mee (31 oktober 2026). */
let voorbeeld = false;
/** @type {ReturnType<typeof setTimeout> | null} */
let wachten = null;   // de korte pauze voor het meteen doorgaan bij één antwoord

/**
 * Antwoorden die niet meer gelden weghalen (5 oktober 2026). Gaat iemand terug en kiest hij een andere hoofdklacht, dan
 * horen de oude vervolgantwoorden er niet meer bij; anders zouden ze in de uitslag blijven meetellen.
 */
function opschonen() {
  for (let ronde = 0; ronde < 3; ronde += 1) {
    const zichtbaar = visible().flatMap(delenVan);
    for (const key of new Set(Q.flatMap((q) => (q.parts ? q.parts : [q]).map((d) => d.key)))) {
      if (state[key] === undefined) continue;
      const vragen = zichtbaar.filter((q) => q.key === key);
      if (!vragen.length) { delete state[key]; continue; }
      const geldig = new Set(vragen.flatMap((q) => (typeof q.opts === 'function' ? q.opts(state) : q.opts).map((o) => o.v)));
      if (Array.isArray(state[key])) {
        const rest = state[key].filter((v) => geldig.has(v));
        if (rest.length) state[key] = rest; else delete state[key];
      } else if (!geldig.has(state[key])) delete state[key];
    }
  }
}
function track(name, data) {
  if (voorbeeld) return;
  try {
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(name, { ...data, language: IS_NL ? 'nl' : 'en' });
    }
  } catch (problem) {
    console.warn('[au-skin-analysis] meten lukte niet', problem);
  }
}
function startRun() {
  voorbeeld = false;
  seen = new Set();
  track('skin_analysis_started', {});
}

function ensureWindow() {
  if (win) return win;
  win = h('div', `au-sa color-${CFG?.scheme || 'scheme-1'}`);
  win.hidden = true;
  win.setAttribute('role', 'dialog');
  win.setAttribute('aria-modal', 'true');
  win.setAttribute('aria-label', L.kicker || t('Skin analysis'));
  win.style.setProperty('--au-sa-progress', `${Number(CFG?.progressHeight || 3)}px`);

  const progress = h('div', 'au-sa__progress');
  progress.append(h('span'));
  /* De naam van de test bovenaan in het midden, waar op de site het logo staat (Jesse, 31 oktober 2026); alleen bij de
     vragen, in de uitslag staat hij in het grijze vlak. */
  const brand = h('p', 'au-sa__brand au-sa__label', L.kicker || '');
  const count = h('p', 'au-sa__count au-sa__label');
  count.setAttribute('aria-live', 'polite');
  const close = h('button', 'au-sa__close');
  close.type = 'button';
  close.setAttribute('aria-label', L.close || t('Close'));
  close.innerHTML = ICON_CLOSE;
  close.addEventListener('click', () => closeAnalysis());

  const quiz = h('div', 'au-sa__quiz');
  /* Links (computer): balken in de stapkleuren, zonder tekst (Jesse, 31 oktober 2026). Elke twee vragen komt er een bij:
     eerst STEP ONE, dan STEP TWO en STEP THREE, als laatste TREATMENT; hoe verder in de vragen, hoe meer balken. De
     beelden uit de instellingen staan hier sinds dan niet meer. */
  const media = h('div', 'au-sa__media');
  const boxes = h('div', 'au-sa__boxes');
  boxes.setAttribute('aria-hidden', 'true');
  for (let i = 1; i <= 4; i += 1) {
    const box = h('span', `au-sa__box au-sa__box--${i}`);
    box.dataset.on = 'false';
    boxes.append(box);
  }
  media.append(boxes);
  const panel = h('div', 'au-sa__panel');
  quiz.append(media, panel);

  const result = h('div', 'au-sa__result');
  result.hidden = true;

  win.append(progress, brand, count, close, quiz, result);
  win.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); closeAnalysis(); }
  });
  document.body.append(win);
  return win;
}

/* De teller rechtsboven: "4/8" (Jesse, 31 oktober 2026), voor schermlezers "Question 4 of 8". Leeg bij de uitslag. */
function setCount(number, of) {
  const count = win?.querySelector('.au-sa__count');
  if (!count) return;
  count.replaceChildren();
  if (!number) return;
  const shown = h('span', '', `${number}/${of}`);
  shown.setAttribute('aria-hidden', 'true');
  count.append(shown, h('span', 'au-sa__sr', sayCount(number, of)));
}

function setProgress(fraction) {
  const bar = win?.querySelector('.au-sa__progress span');
  if (bar instanceof HTMLElement) bar.style.width = `${Math.max(2, Math.min(100, fraction * 100))}%`;
}

/* Vraag 1 en 2 één balk, 3 en 4 twee, 5 en 6 drie, vanaf 7 alle vier. Terug gaan haalt ze weer weg. */
function showBoxes() {
  const n = Math.min(4, Math.floor(index / 2) + 1);
  win?.querySelectorAll('.au-sa__box').forEach((box, i) => { box.dataset.on = String(i < n); });
}

function renderQuestion() {
  if (wachten) { clearTimeout(wachten); wachten = null; }
  const list = visible();
  if (index >= list.length) { renderResult(); return; }
  const q = list[index];
  const quiz = win.querySelector('.au-sa__quiz');
  const result = win.querySelector('.au-sa__result');
  quiz.hidden = false;
  result.hidden = true;
  win.dataset.view = 'quiz';
  chatLift(null);
  showBoxes();
  const n = total();
  setProgress((index + 1) / (n + 1));
  setCount(index + 1, n);
  /* Eén melding per plek in de vragenlijst, ook als iemand terug en weer vooruit gaat (5 oktober 2026). */
  if (!seen.has(index + 1)) {
    seen.add(index + 1);
    track('skin_analysis_question_viewed', { question: q.id, number: index + 1, total: n });
  }

  const panel = win.querySelector('.au-sa__panel');
  panel.replaceChildren();
  panel.scrollTop = 0;
  /* Letters (Jesse, 31 oktober 2026): de teller en "Why this question?" als het kleine kopje van de uitslag (de naam van
     de test staat sinds 31 oktober 2026 bovenaan, zie ensureWindow); de vraag en de kopjes van de delen kleiner en niet in hoofdletters, in de stijl van de instelling
     question_style (standaard Heading 5); hints, meldingen en uitleg in lopende tekst. */
  const inner = h('div', 'au-sa__inner');
  const title = h('h2', `au-sa__question ${S.question || 'h5'}`, t(typeof q.text === 'function' ? q.text(state) : q.text));
  title.id = 'au-sa-question';
  title.tabIndex = -1;
  inner.append(title);
  if (q.kind === 'multi' || q.hint) inner.append(h('p', `au-sa__hint ${S.body || 'paragraph'}`, q.hint ? t(q.hint) : L.multi || ''));

  /* Een vraag in delen (vraag 5 en 8, 5 oktober 2026) krijgt per deel een kopje en eigen knoppen; een deel dat niet
     geldt (exfoliëren bij een huid die vaak reageert), staat er niet. */
  const vakken = [];
  /* Volgende is altijd te klikken (Jesse, 7 oktober 2026). Mist er nog iets, dan wordt de knop rood en staat onder
     elk deel dat bij die klik nog leeg was in het kort wat er nog gekozen moet worden; dat verdwijnt zodra het gekozen
     is. Een deel dat daarna pas verschijnt (exfoliëren na "nee"), krijgt pas een melding bij de volgende klik. */
  let gemist = new Set();
  const bijwerken = () => {
    for (const vak of vakken) {
      const geldt = !(vak.deel.when && !vak.deel.when(state));
      vak.blok.hidden = !geldt;
      for (const knop of vak.blok.querySelectorAll('.au-sa__answer')) {
        const w = state[vak.deel.key];
        knop.setAttribute('aria-pressed', String(vak.deel.kind === 'multi' ? has(w, knop.dataset.value) : w === knop.dataset.value));
      }
      vak.melding.hidden = !(gemist.has(vak) && geldt && !deelAf(vak.deel));
    }
    const af = answered(q);
    next.classList.toggle('au-sa__next--open', !af);
    next.classList.toggle('au-sa__next--fout', vakken.some((v) => !v.melding.hidden));
    next.setAttribute('aria-disabled', String(!af));
  };
  (q.parts || [q]).forEach((deel, nr) => {
    const blok = h('div', 'au-sa__part');
    /* Een deel zonder tekst (het eerste deel van vraag 5: de vraag zelf is de hoofdvraag) krijgt geen kopje. Een deel
       met een korte kop (exfoliëren) krijgt de kop en de vraag eronder. */
    if (q.parts && (deel.text || deel.head)) {
      const kop = h('h3', `au-sa__parttitle ${S.question || 'h5'}`, t(deel.head || deel.text));
      kop.id = `au-sa-part-${nr}`;
      blok.append(kop);
      if (deel.head && deel.text) blok.append(h('p', `au-sa__hint ${S.body || 'paragraph'}`, t(deel.text)));
      if (deel.kind === 'multi') blok.append(h('p', `au-sa__hint ${S.body || 'paragraph'}`, deel.hint ? t(deel.hint) : L.multi || ''));
    }
    const choices = typeof deel.opts === 'function' ? deel.opts(state) : deel.opts;
    const answers = h('div', `au-sa__answers${choices.length > 10 ? ' au-sa__answers--two' : ''}`);
    answers.setAttribute('role', 'group');
    answers.setAttribute('aria-labelledby', q.parts && (deel.text || deel.head) ? `au-sa-part-${nr}` : 'au-sa-question');
    for (const choice of choices) {
      const button = h('button', `au-sa__answer ${S.answer || 'paragraph'}`, t(choice.t));
      button.type = 'button';
      button.dataset.value = choice.v;
      button.addEventListener('click', () => {
        if (deel.kind === 'multi') {
          let current = Array.isArray(state[deel.key]) ? [...state[deel.key]] : [];
          if (choice.v === 'none' || (choice.v === 'unknown' && !deel.one)) current = [choice.v];
          else {
            current = current.filter((x) => x !== 'none' && !(x === 'unknown' && !deel.one));
            const at = current.indexOf(choice.v);
            if (at > -1) current.splice(at, 1);
            else {
              /* Binnen "one" (de zon, of de textuur) kan er maar één aan staan; de rest mag erbij. */
              if (deel.one && deel.one.includes(choice.v)) current = current.filter((x) => !deel.one.includes(x));
              current.push(choice.v);
            }
          }
          state[deel.key] = current;
          opschonen();
          bijwerken();
        } else {
          state[deel.key] = choice.v;
          opschonen();
          bijwerken();
          /* Bij één antwoord meteen door (Jesse, 5 oktober 2026): heel even laten zien wat gekozen is, dan de volgende vraag.
             Niet bij meerkeuze, en niet bij een vraag in delen: daar klik je altijd zelf op Volgende. */
          if (!q.parts) {
            if (wachten) clearTimeout(wachten);
            wachten = setTimeout(() => { wachten = null; verder(); }, 250);
          }
        }
      });
      answers.append(button);
    }
    blok.append(answers);
    const melding = h('p', `au-sa__need ${S.body || 'paragraph'}`,
      t(deel.need || (deel.kind === 'multi' ? 'Choose at least one answer.' : 'Choose an answer.')));
    melding.hidden = true;
    melding.setAttribute('aria-live', 'polite');
    blok.append(melding);
    vakken.push({ deel, blok, melding });
    inner.append(blok);
  });

  const nav = h('div', 'au-sa__nav');
  const prev = h('button', 'button button-secondary', L.previous || t('Previous'));
  prev.type = 'button';
  prev.addEventListener('click', () => {
    if (index === 0) closeAnalysis();
    else { index -= 1; renderQuestion(); }
  });
  const next = h('button', 'button', index === list.length - 1 ? L.results || t('See my results') : L.next || t('Next'));
  next.type = 'button';
  bijwerken();
  function verder() {
    if (!answered(q)) {
      gemist = new Set(vakken.filter((v) => !(v.deel.when && !v.deel.when(state)) && !deelAf(v.deel)));
      bijwerken();
      const eerste = vakken.find((v) => !v.melding.hidden);
      if (eerste) eerste.melding.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }
    index += 1;
    renderQuestion();
  }
  next.addEventListener('click', () => {
    if (wachten) { clearTimeout(wachten); wachten = null; }
    verder();
  });
  nav.append(prev, next);
  inner.append(nav);

  const why = h('div', 'au-sa__why');
  why.append(h('p', 'au-sa__label', L.why || ''), h('p', S.body || 'paragraph', t(q.why)));
  inner.append(why);

  panel.append(inner);
  requestAnimationFrame(() => title.focus({ preventScroll: true }));
}

/* Het vinkje van de productpagina. */
const ICON_CHECK = '<svg viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 7.5l3 3 6-7"/></svg>';

/* Het pijltje van het aanmeldveld in de footer (email-signup van Vessel). */
const ICON_ARROW = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path clip-rule="evenodd" fill-rule="evenodd" fill="currentColor" d="m11.246 5.31759c.0322-.07821.0833-.14723.1486-.20093.0654-.0537.143-.09041.2259-.10686.083-.01645.1688-.01214.2497.01257.0808.0247.1544.06902.214.12902l4.104 4.104c.0478.04765.0857.10426.1115.16659.0259.06232.0392.12913.0392.19661 0 .06747-.0133.13429-.0392.19661-.0258.06233-.0637.11893-.1115.16659l-4.104 4.10401c-.0963.0963-.227.1504-.3632.1504s-.2669-.0541-.3632-.1504-.1504-.227-.1504-.3632.0541-.2669.1504-.3632l3.2288-3.2278h-11.0736c-.13606 0-.26654-.0541-.36275-.15027-.0962-.0962-.15025-.22669-.15025-.36274 0-.13606.05405-.26654.15025-.36275.09621-.0962.22669-.15025.36275-.15025h11.0736l-3.2288-3.2278c-.0721-.07178-.1212-.16335-.1411-.2631s-.0097-.20316.0293-.2971z"/></svg>';

/* Het plusje van de uitklaprijen op de productpagina. */
const ICON_PLUS = '<svg viewBox="0 0 14 14" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2.75 7H11.25"/><path class="au-sa__howv" d="M7 2.75V11.25"/></svg>';

/* De stapnaam uit STEP_OF, ook voor het vierde product (Jesse, 30 oktober 2026: niet "And" maar de stap, in volgorde). */
const STEPNAME = { 1: 'Step one', 2: 'Step two', 3: 'Step three', 0: 'Treatment' };
const STEPORDER = { 1: 1, 2: 2, 3: 3, 0: 4 };

/**
 * Het grijze vlak met de foto, zoals de productfoto's op de site (#F4F4F4, hoeken 10).
 * @param {Product} p
 */
function tile(p) {
  const link = h('a', 'au-sa__tile');
  link.href = p.url;
  link.tabIndex = -1;
  link.setAttribute('aria-hidden', 'true');
  if (p.image) {
    const img = h('img');
    img.src = p.image; img.alt = ''; img.loading = 'lazy';
    link.append(img);
  }
  return link;
}

/**
 * Een kaartje bij "All products that suit you", zoals op de collectiepagina's (Jesse, 30 oktober 2026; templates/
 * collection.json): de foto 4:5 met hoeken 10, de naam en daaronder product_name | inside in het midden, de prijs, en
 * de aantalkiezer met Add to bag van het thema (dezelfde klassen, iconen en tekst).
 * @param {Product} p
 */
function productCard(p) {
  const card = h('div', 'au-sa__card');
  const theme = CFG?.theme || {};
  const photo = tile(p);
  const name = h('a', 'au-sa__cardtitle');
  name.href = p.url;
  name.textContent = p.title;
  card.append(photo, name);
  const line = [p.line, p.inside].filter(Boolean).join(' | ');
  if (line) card.append(h('p', 'au-sa__cardline', line));
  card.append(h('p', 'au-sa__cardprice', money(p.price)));

  const buy = h('div', 'product-form-buttons au-sa__buy');
  const wrap = h('div', 'quantity-selector-wrapper');
  const qty = h('div', 'quantity-selector');
  const input = h('input');
  input.type = 'number'; input.min = '1'; input.step = '1'; input.value = '1';
  input.setAttribute('aria-label', theme.quantity || 'Quantity');
  const amount = () => Math.max(1, Math.floor(Number(input.value) || 1));
  const stepper = (cls, label, icon, by) => {
    const button = h('button', `button ${cls} button-unstyled`);
    button.type = 'button';
    button.append(h('span', 'visually-hidden', label));
    const svg = h('span', 'svg-wrapper');
    svg.innerHTML = icon || '';
    button.append(svg);
    button.addEventListener('click', () => { input.value = String(Math.max(1, amount() + by)); });
    return button;
  };
  qty.append(stepper('quantity-minus', theme.minus || 'Decrease quantity', theme.iconMinus, -1), input,
    stepper('quantity-plus', theme.plus || 'Increase quantity', theme.iconPlus, 1));
  input.addEventListener('blur', () => { input.value = String(amount()); });
  wrap.append(qty);
  const add = h('button', 'button add-to-cart-button button-secondary', theme.addToBag || t('Add to bag'));
  add.type = 'button';
  add.addEventListener('click', () => addToCart([p], add, undefined, amount()));
  const holder = h('span', 'au-sa__buyadd');
  holder.append(add);
  buy.append(wrap, holder);
  card.append(buy);
  return card;
}

/* Hoe je elk product aanbrengt, in het ritual (Jesse, 31 oktober 2026). Kort, uit how_to_use in producten.md, maar
   zonder andere producten: how_to_use op de productpagina noemt een nachtcrème, oogcrème of SPF die niet in elk
   ritual zitten ("de teksten en de routines moeten wel op elkaar aansluiten"). De volgorde staat in de lijst erboven. */
const RITUAL_USE = {
  foam: 'Work a small amount over a damp face in gentle circles, then rinse with lukewarm water and pat dry.',
  milk: 'Massage one or two pumps onto dry or damp skin in gentle circles, then rinse with lukewarm water and pat dry.',
  oilcl: 'Massage one pump onto dry skin to dissolve make-up, add a splash of water so the oil turns into a milk, then rinse with lukewarm water and pat dry.',
  pore: 'Apply with a cotton pad or your hands, with extra attention for the T-zone, and let it dry. Do not rinse.',
  hyal: 'Apply with a cotton pad or press it in with clean hands, and let it absorb. Do not rinse.',
  polish: 'Massage onto damp skin in gentle circles for 30 to 60 seconds, away from the eyes, then rinse with lukewarm water and pat dry.',
  aha: 'Press a few drops into clean, dry skin, away from the eyes, and leave it on overnight. A light tingle is normal.',
  goldenc: 'Press one pump into clean skin and wait a minute before the next step.',
  peptide: 'Press one pump into clean skin and wait a minute before the next step.',
  bioactive: 'Press a few drops into clean skin and wait a minute before the next step.',
  bakuchiol: 'Press a few drops into clean skin.',
  ageless: 'Press one pump into clean skin and wait a minute before the next step.',
  pigment: 'Press one pump into clean skin and wait a minute before the next step.',
  eyecream: 'Tap a small amount around the eyes with your ring finger, without touching the eyes themselves, and let it absorb.',
  eyeserum: 'Tap a small amount around the eyes with your ring finger, from the inner corner outward, without touching the eyes themselves, and let it absorb.',
  daycream: 'Warm one pump between your fingertips and press it onto your skin. Let it absorb before make-up.',
  moisture: 'Warm one pump between your fingertips and press it onto your skin. Let it absorb before make-up.',
  night: 'Warm one pump between your fingertips and press it onto your skin.',
  serenity: 'Warm one pump between your fingertips and press it onto your skin.',
  clarity: 'Warm one pump between your fingertips and press it onto your skin.',
  oil: 'Warm three to four drops between your palms and press them onto your skin.',
  stick: 'Apply generously and blend with your fingertips, over make-up too. Reapply at least every two hours, and after swimming, sweating or towel drying.',
  fluid: 'Spread a generous amount evenly over your skin as the last step of the morning. Reapply after swimming, sweating or towel drying.',
  mask: 'Apply a generous layer to clean, dry skin, away from the eyes. Leave it on for 5 to 10 minutes, then rinse with lukewarm water and pat dry.',
  booster: 'Mix 1 to 3 drops into your serum or cream in the palm of your hand, then apply as usual.',
};
/* Hoe vaak, voor wat je niet elke dag gebruikt (when_to_use_tekst en faq_1 in producten.md). */
const OFTEN = {
  polish: 'Two or three times a week, morning or evening, never two days in a row.',
  aha: 'One or two evenings a week, never two evenings in a row, and wear sun protection the next morning.',
  mask: 'Once or twice a week, morning or evening, after cleansing.',
};

/**
 * How to use this ritual (Jesse, 31 oktober 2026: "wanneer de routine gebruikt moet worden en hoe en in welke volgorde"):
 * eerst wat je elke ochtend en elke avond gebruikt, op de volgorde van het ritual (MOMENT_OF; ALL DAY staat bij
 * allebei); dan wat je niet elke dag gebruikt, met hoe vaak (OFTEN); dan per product hoe je het aanbrengt (RITUAL_USE).
 * @param {Product[]} products in de volgorde van het ritual
 * @returns {{moments: [string, Product[]][], sometimes: [Product, string][], uses: [Product, string][]}}
 */
function howToPlan(products) {
  const sometimes = products.filter((p) => MOMENT_OF[p.key] === 'week');
  const daily = products.filter((p) => !sometimes.includes(p));
  const at = (moment) => daily.filter((p) => !MOMENT_OF[p.key] || MOMENT_OF[p.key] === moment || MOMENT_OF[p.key] === 'both');
  return {
    moments: [[t('Every morning'), at('morning')], [t('Every evening'), at('evening')]].filter(([, list]) => list.length),
    sometimes: sometimes.map((p) => [p, t(OFTEN[p.key] || '')]),
    uses: products.filter((p) => RITUAL_USE[p.key]).map((p) => [p, t(RITUAL_USE[p.key])]),
  };
}

/**
 * Een ritual in ontwerp C (Jesse, 30 oktober 2026): de titel in zwart met het labeltje Save ernaast, per stap de foto,
 * de stap (sinds 31 oktober 2026 zonder ochtend of avond), de naam, de korte regel en de prijs; daaronder het totaal
 * met de korting, de knop en een uitklaprij "How to use this ritual" (zie howToPlan). Het complete ritual ziet er
 * precies zo uit. Geeft ook de gegevens voor de mail terug.
 * @param {string} title
 * @param {Array<[Product, string, string?]>} rows
 * @param {boolean} discount
 */
function ritualBlock(title, rows, discount) {
  const block = h('section', 'au-sa__ritual');
  const percent = Number(CFG?.percent || 0);
  const korting = discount && percent > 0;
  const saveText = t(L.saveTag || 'Save [percent]%').replace('[percent]', String(percent));
  const head = h('div', 'au-sa__ritualhead');
  head.append(h('h3', `au-sa__heading ${S.title || 'h1'}`, title));
  if (korting) head.append(h('span', 'au-sa__savetag', saveText));
  block.append(head);
  const now = rows.filter(([p]) => p && p.available);
  const steps = h('div', 'au-sa__steps');
  for (const [p, step, reason] of now) {
    const row = h('div', 'au-sa__step');
    const body = h('div', 'au-sa__stepbody');
    body.append(h('p', 'au-sa__steplabel', step));
    const name = h('a', 'au-sa__name');
    name.href = p.url;
    name.append(h('span', S.small || 'h6', p.title));
    body.append(name);
    if (p.line) body.append(h('p', `au-sa__line ${S.body || 'paragraph'} au-small`, p.line));
    if (reason) body.append(h('p', `au-sa__reason ${S.body || 'paragraph'} au-small`, `${L.because || t('Because')} ${t(reason)}.`));
    row.append(tile(p), body, h('p', `au-sa__price ${S.body || 'paragraph'}`, money(p.price)));
    steps.append(row);
  }

  const total = now.reduce((sum, [p]) => sum + p.price, 0);
  /* Shopify rondt de korting naar beneden af, dus de prijs volgt daaruit (gemeten 16 september 2026). De korting
     geldt voor de hele winkelmand zodra stap 1, 2 en 3 erin zitten. */
  const pay = korting ? total - Math.floor((total * percent) / 100) : total;
  const totals = h('div', 'au-sa__totals');
  /* Links de oude prijs, rechts wat je betaalt, zoals in de ritual op de productpagina (Jesse, 30 oktober 2026). */
  const prices = h('p', 'au-sa__sum');
  if (korting) prices.append(h('s', `au-sa__was ${S.body || 'paragraph'}`, money(total)));
  prices.append(h('span', `au-sa__pay ${S.small || 'h6'}`, money(pay)));
  totals.append(prices);
  let saveLine = '';
  if (korting) {
    const bespaar = h('p', `au-sa__save ${S.body || 'paragraph'} au-small`);
    const [voor, na] = (L.save || '').split('[amount]');
    bespaar.append(document.createTextNode(voor || ''));
    if (na !== undefined) bespaar.append(h('span', 'au-sa__saveamount', money(total - pay)), document.createTextNode(na));
    totals.append(bespaar);
    saveLine = bespaar.textContent;
  } else if (!discount) {
    totals.append(h('p', `au-sa__save ${S.body || 'paragraph'} au-small`, L.noDiscount || ''));
    saveLine = L.noDiscount || '';
  }
  const add = h('button', 'button au-sa__addritual', L.addRoutine || t('Add ritual to cart'));
  add.type = 'button';
  const error = h('p', `au-sa__error ${S.body || 'paragraph'} au-small`);
  error.hidden = true;
  error.setAttribute('role', 'alert');
  add.addEventListener('click', () => addToCart(now.map(([p]) => p), add, error));
  /* De knop en de uitklaprij: op de computer naast elkaar, op de telefoon onder elkaar (Jesse, 31 oktober 2026). */
  const actions = h('div', 'au-sa__actions');
  actions.append(add);
  totals.append(actions);
  block.append(steps, totals, error);

  /* Onder de knop: hoe je het ritual gebruikt (Jesse, 30 en 31 oktober 2026), dicht bij het openen. */
  const plan = howToPlan(now.map(([p]) => p));
  if (plan.moments.length || plan.sometimes.length || plan.uses.length) {
    const how = h('details', 'au-sa__how');
    const summary = h('summary', `au-sa__howhead ${S.body || 'paragraph'}`);
    const icon = h('span', 'au-sa__howicon');
    icon.innerHTML = ICON_PLUS;
    summary.append(h('span', '', t(L.how || 'How to use this ritual')), icon);
    const list = h('div', 'au-sa__howbody');
    const group = (label) => {
      const item = h('div', 'au-sa__howstep');
      item.append(h('p', 'au-sa__steplabel', label));
      list.append(item);
      return item;
    };
    const line = (name, text) => {
      const p = h('p', `${S.body || 'paragraph'} au-small`);
      p.append(h('b', '', `${name}. `), document.createTextNode(text));
      return p;
    };
    for (const [label, items] of plan.moments) {
      const ol = h('ol', `au-sa__howlist ${S.body || 'paragraph'} au-small`);
      items.forEach((p) => ol.append(h('li', '', p.title)));
      group(label).append(ol);
    }
    if (plan.sometimes.length) {
      const item = group(t('Not every day'));
      plan.sometimes.forEach(([p, text]) => item.append(line(p.title, text)));
    }
    if (plan.uses.length) {
      const item = group(t('How to apply'));
      plan.uses.forEach(([p, text]) => item.append(line(p.title, text)));
    }
    how.append(summary, list);
    actions.append(how);
  }

  /* Voor de mail: volledige adressen, en de foto als jpg (niet elke mailapp toont webp). */
  const abs = (url) => (!url ? '' : url.startsWith('//') ? `https:${url}` : url.startsWith('/') ? `${location.origin}${url}` : url);
  const mail = {
    Title: title, Save: korting ? saveText : '', Was: korting ? money(total) : '', Price: money(pay), SaveLine: saveLine,
    /* Het ritual in één keer in de checkout, met de ritualkorting (gemeten 30 oktober 2026: −€23,24 op €154,97). */
    CartUrl: `${location.origin}/cart/${now.map(([p]) => `${p.variant}:1`).join(',')}`,
    HowTo: {
      Moments: plan.moments.map(([Label, items]) => ({ Label, Names: items.map((p) => p.title) })),
      Sometimes: plan.sometimes.map(([p, When]) => ({ Name: p.title, When })),
      Uses: plan.uses.map(([p, HowToUse]) => ({ Name: p.title, HowToUse })),
    },
    Products: now.map(([p, step, reason]) => ({
      Step: step, Name: p.title, Line: p.line, Price: money(p.price), Url: abs(p.url), Image: p.image ? `${abs(p.image)}${p.image.includes('?') ? '&' : '?'}format=pjpg` : '',
      Reason: reason ? `${L.because || t('Because')} ${t(reason)}.` : '', HowToUse: p.use || '',
    })),
  };
  return { block, pay, mail, products: now.map(([p]) => p) };
}

/**
 * De uitslag mailen (Jesse, 30 oktober 2026): in het grijze vlak boven Start again een kopje, het veld en eronder de
 * regel dat je je bij versturen ook inschrijft voor de e-maillijst (geen vinkje, besluit Jesse). Bij versturen gaat een
 * melding naar Klaviyo (metric "Skin Analysis Results", met de uitslag erin); een flow in Klaviyo mailt de uitslag. Het
 * adres gaat ook naar de e-maillijst; die heeft dubbele bevestiging, dus nieuwsbrieven pas na de klik in de
 * bevestigingsmail. Staat alleen aan met de instelling mail_enabled.
 * @param {ReturnType<typeof build>} result
 * @param {Array<object>} rituals - de gegevens van de rituals voor de mail
 */
function mailBlock(result, rituals) {
  const block = h('div', 'au-sa__mail');
  block.append(h('p', 'au-sa__label', L.mailButton || t('Email my results')));
  /* Het veld zoals het aanmeldveld in de footer (Jesse, 30 oktober 2026): één vlak met een zwarte rand, hoeken 10,
     rechts het pijltje. */
  const form = h('form', 'au-sa__mailrow');
  form.noValidate = true;
  const input = h('input', 'paragraph');
  input.type = 'email'; input.required = true; input.id = 'au-sa-mail'; input.name = 'email';
  input.autocomplete = 'email'; input.placeholder = L.mailPlaceholder || '';
  input.setAttribute('aria-label', L.mailPlaceholder || t('Email'));
  const send = h('button', 'au-sa__mailsend');
  send.type = 'submit';
  send.setAttribute('aria-label', t(L.mailSend || 'Send'));
  send.innerHTML = ICON_ARROW;
  form.append(input, send);
  const fine = h('p', `au-sa__fine ${S.body || 'paragraph'} au-small`);
  const [before, after] = t(L.mailSignup || '').split('[privacy]');
  fine.append(document.createTextNode(before || ''));
  if (after !== undefined) {
    if (CFG?.privacyUrl) { const a = h('a', '', L.privacyLink || t('privacy policy')); a.href = CFG.privacyUrl; fine.append(a); }
    else fine.append(document.createTextNode(L.privacyLink || t('privacy policy')));
    fine.append(document.createTextNode(after));
  }
  const status = h('p', `au-sa__mailstatus ${S.body || 'paragraph'} au-small`);
  status.hidden = true;
  status.setAttribute('role', 'status');
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!input.checkValidity()) { input.reportValidity(); return; }
    send.disabled = true;
    status.hidden = true;
    try {
      await mailResults(input.value.trim(), true, result, rituals);
      form.hidden = true;
      status.textContent = t(L.mailDone || 'Your results are on their way to your inbox.');
      status.hidden = false;
      track('skin_analysis_mailed', {});
    } catch (problem) {
      console.error('[au-skin-analysis]', problem);
      status.textContent = L.error || String(problem);
      status.hidden = false;
      send.disabled = false;
    }
  });
  block.append(form, fine, status);
  return block;
}

/** De melding naar Klaviyo, en het adres op de e-maillijst (met dubbele bevestiging). */
async function mailResults(email, marketing, result, rituals) {
  const company = CFG?.klaviyo?.company;
  if (!company) throw new Error('Klaviyo is not set up');
  const url = (path) => `https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(company)}`;
  const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };
  const now = new Date();
  const properties = {
    Goal: t(result.goal),
    SkinType: sayType(result.typename).trim(),
    SkinTypeText: t(result.typeline),
    Conditions: result.conditions?.length ? sayConditions(result.conditions).trim() : '',
    Plan: t(result.plan),
    Notes: result.notes.map(t),
    Skin: skinTicks(result).map(([Head, Text]) => ({ Head, Text })),
    Rituals: rituals,
    Language: IS_NL ? 'nl' : 'en',
    PageUrl: `${location.origin}${location.pathname}`,
    Marketing: marketing,
  };
  const event = { data: { type: 'event', attributes: {
    properties,
    metric: { data: { type: 'metric', attributes: { name: 'Skin Analysis Results' } } },
    /* De uitslag ook bij het profiel (30 oktober 2026): wie mailt voordat de flow in Klaviyo live staat, kan hem
       daarmee later alsnog krijgen. */
    profile: { data: { type: 'profile', attributes: { email, properties: { 'Skin Analysis': properties, 'Skin Analysis Date': now.toISOString() } } } },
    time: now.toISOString(),
    unique_id: `sa-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`,
  } } };
  const response = await fetch(url('events'), { method: 'POST', headers, body: JSON.stringify(event) });
  if (!response.ok) throw new Error(`Klaviyo answered ${response.status}`);
  if (marketing && CFG?.klaviyo?.list) {
    const subscription = { data: { type: 'subscription', attributes: {
      custom_source: 'Skin analysis',
      profile: { data: { type: 'profile', attributes: { email } } },
    }, relationships: { list: { data: { type: 'list', id: CFG.klaviyo.list } } } } };
    const sub = await fetch(url('subscriptions'), { method: 'POST', headers, body: JSON.stringify(subscription) });
    if (!sub.ok) console.error('[au-skin-analysis] Klaviyo subscription', sub.status);
  }
  document.dispatchEvent(new CustomEvent('au-skin-analysis:mail', { detail: { marketing, answers: { ...state } } }));
}

let resultWatch = null;
/** @type {ResizeObserver | null} */
let sideWatch = null;

/* Het chatwolkje van Shopify Inbox schuift boven de balk onderin, zolang die er staat (zoals bij het balkje op de
   productpagina, 17 oktober 2026). */
function chatLift(bar) {
  const on = Boolean(bar && bar.dataset.hide !== 'true' && bar.offsetParent);
  document.documentElement.classList.toggle('au-sa-bar-on', on);
  if (on) document.documentElement.style.setProperty('--au-sa-chat-lift', `${bar.offsetHeight + 20}px`);
}

function renderResult() {
  const r = build(state);
  const quiz = win.querySelector('.au-sa__quiz');
  const result = win.querySelector('.au-sa__result');
  quiz.hidden = true;
  result.hidden = false;
  win.dataset.view = 'result';
  setProgress(1);
  setCount(0);
  track('skin_analysis_completed', {
    questions: visible().length, goal: r.goal, skin_type: r.typename,
    products: [r.one, r.two, r.three].filter(Boolean).map((p) => p.handle),
  });
  if (resultWatch) { resultWatch.disconnect(); resultWatch = null; }
  if (sideWatch) { sideWatch.disconnect(); sideWatch = null; }
  result.replaceChildren();
  result.scrollTop = 0;

  /* Ontwerp C, het rapport (Jesse, 30 oktober 2026): links een grijs vlak met wat we zien, dat op de computer met de
     uitslag meeschuift; rechts het ritual en het complete ritual; daaronder over de hele breedte alle producten die bij
     je passen. Tussen die drie de ruimte uit de instelling result_gap (standaard 100 px, ook op de telefoon). */
  const inner = h('div', 'au-sa__rap');
  inner.style.setProperty('--au-sa-gap', `${Number(CFG?.gap ?? 100)}px`);
  inner.style.setProperty('--au-sa-btn-w', `${Number(CFG?.buttonWidth ?? 25)}%`);
  inner.style.setProperty('--au-sa-btn-n', String(Number(CFG?.buttonWidth ?? 25)));
  const top = h('div', 'au-sa__raptop');

  const side = h('aside', 'au-sa__side');
  side.append(h('p', 'au-sa__label', L.kicker || ''));
  const heading = h('h2', `au-sa__heading ${S.title || 'h1'}`, t(r.goal));
  heading.tabIndex = -1;
  side.append(heading);
  if (r.tags.length) {
    const tags = h('div', 'au-sa__tags');
    r.tags.forEach((tag) => tags.append(h('span', `au-sa__tag ${S.body || 'paragraph'} au-small`, t(tag))));
    side.append(tags);
  }
  side.append(h('p', `au-sa__plan ${S.body || 'paragraph'}`, t(r.plan)), h('hr', 'au-sa__rule'));
  side.append(h('p', 'au-sa__label', t(L.skin || 'Your skin')));
  const ticks = h('ul', 'au-sa__ticks');
  const tick = (bold, text) => {
    const li = h('li', `au-sa__tick ${S.body || 'paragraph'}`);
    const icon = h('span', 'au-sa__tickicon');
    icon.innerHTML = ICON_CHECK;
    const words = h('span');
    if (bold) words.append(h('b', '', bold.trim()), document.createTextNode(' '));
    words.append(document.createTextNode(text));
    li.append(icon, words);
    ticks.append(li);
  };
  skinTicks(r).forEach(([bold, text]) => tick(bold, text));
  side.append(ticks);
  /* Start again is weg (Jesse, 31 oktober 2026); opnieuw beginnen kan door de test opnieuw te openen. */
  const mailPlek = h('div', 'au-sa__mailplek');
  side.append(mailPlek);

  const main = h('div', 'au-sa__main');
  const rows = [[r.one, t('Step one')], [r.two, t('Step two')], [r.three, t('Step three')]].filter(([p]) => p);
  const basis = ritualBlock(r.discount ? L.threeTitle || '' : L.shortTitle || '', rows, r.discount);
  main.append(basis.block);
  const rituals = [basis.mail];
  if (r.discount && r.four) {
    const [vier, reden] = r.four;
    const stap = STEP_OF[vier.key] ?? 3;
    const rows4 = [...rows.map((row, i) => [...row, undefined, i]), [vier, t(STEPNAME[stap]), reden, 9]];
    const plek = (row) => (row[0] === vier ? STEPORDER[stap] : STEPORDER[STEP_OF[row[0].key]] ?? row[3] + 1);
    rows4.sort((a, b) => plek(a) - plek(b) || a[3] - b[3]);
    const compleet = ritualBlock(L.fourTitle || '', rows4.map(([p, step, reason]) => [p, step, reason]), true);
    main.append(compleet.block);
    rituals.push(compleet.mail);
  }
  if (CFG?.mail) mailPlek.append(h('hr', 'au-sa__rule'), mailBlock(r, rituals));
  top.append(side, main);
  inner.append(top);

  /* Alle producten die bij je passen (Jesse, 30 oktober 2026), ook die van je ritual. */
  const picks = r.picks.filter((p) => p.available);
  if (picks.length) {
    const all = h('section', 'au-sa__all');
    all.append(h('p', 'au-sa__label', t(L.all || 'All products that suit you')),
      h('h3', `au-sa__heading ${S.title || 'h1'}`, t(L.allTitle || 'Chosen for your skin')));
    const cards = h('div', 'au-sa__cards');
    picks.forEach((p) => cards.append(productCard(p)));
    all.append(cards);
    inner.append(all);
  }


  /* Telefoon: onderin een balk met het ritual en de knop, pas als het ritual van drie boven uit beeld is (Jesse,
     30 oktober 2026). */
  if (basis.products.length) {
    const bar = h('div', 'au-sa__bar');
    const barText = h('div', 'au-sa__bartext');
    barText.append(h('p', 'au-sa__label', r.discount ? L.threeTitle || '' : L.shortTitle || ''),
      h('p', `au-sa__pay ${S.small || 'h6'}`, money(basis.pay)));
    const barAdd = h('button', 'button', t(L.addBar || 'Add ritual'));
    barAdd.type = 'button';
    barAdd.addEventListener('click', () => addToCart(basis.products, barAdd));
    bar.append(barText, barAdd);
    bar.dataset.hide = 'true';
    inner.append(bar);
    if ('IntersectionObserver' in window) {
      resultWatch = new IntersectionObserver(([entry]) => {
        const voorbij = !entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds ? entry.rootBounds.top : 0);
        bar.dataset.hide = String(!voorbij);
        chatLift(bar);
      }, { root: result });
      resultWatch.observe(basis.block);
    } else {
      bar.dataset.hide = 'false';
    }
  }

  result.append(inner);
  chatLift(inner.querySelector('.au-sa__bar'));
  /* De uitklaprij naast de knop even hoog als de knop van het thema. */
  inner.querySelectorAll('.au-sa__actions').forEach((row) => {
    const button = row.querySelector('.au-sa__addritual');
    if (button instanceof HTMLElement && button.offsetHeight) row.style.setProperty('--au-sa-btn-h', `${button.offsetHeight}px`);
  });

  /* Het grijze vlak (computer): past het niet op het scherm, dan schuift het mee tot zijn onderkant 40 px boven de rand
     staat en blijft het daar staan, in plaats van een eigen schuifbalk (31 oktober 2026, langere vinkjes). */
  if ('ResizeObserver' in window) {
    const plaats = () => {
      side.style.setProperty('--au-sa-side-top', `${Math.min(72, result.clientHeight - side.offsetHeight - 40)}px`);
    };
    sideWatch = new ResizeObserver(plaats);
    sideWatch.observe(side);
    sideWatch.observe(result);
  }
  requestAnimationFrame(() => heading.focus({ preventScroll: true }));
}

/* ------------------------------------------------------------------ */
/* 6. Openen en sluiten                                                */
/* ------------------------------------------------------------------ */

const HASH = '#analysis';
const EXAMPLE = '#analysis-example';

/**
 * De voorbeelduitslag (Jesse, 31 oktober 2026): met #analysis-example achter het adres, of in de editor als "In de
 * editor tonen" op de voorbeelduitslag staat. Vaste antwoorden die alles van de uitslag laten zien: een ritual van drie
 * met korting, het complete ritual met een vierde product en zijn reden, een aandachtspunt en de kaartjes van alle
 * producten die passen (fijne lijntjes, normale huid, make-up, af en toe zon, een rijke crème en vooral 's avonds:
 * oil cleanser, Ageless Renewal Serum, nachtcrème en het oogserum). Telt niet mee in de metingen.
 */
const VOORBEELD_ANTWOORDEN = {
  top: 'lines', more: ['dullness', 'eyelines'], why: ['all'], skintype: 'normal', reacts: 'no', exfo: 'acid',
  finish: ['rich', 'evening'], preg: 'no', day: ['some', 'makeup'], avoid: ['none'],
};

/* Zelf in de geschiedenis gezet? Dan sluiten met terug; kwam het adres binnen met #analysis, dan alleen het stukje
   achter # weghalen, anders ga je bij sluiten de site af. */
let pushed = false;

/** @param {boolean} [push] @param {'example'} [view] */
function openAnalysis(push = true, view) {
  if (!CFG) return;
  ensureWindow();
  if (!win.hidden) return;
  returnFocus = document.activeElement;
  win.hidden = false;
  document.documentElement.classList.add('au-sa-lock');
  if (view === 'example') {
    state = JSON.parse(JSON.stringify(VOORBEELD_ANTWOORDEN));
    index = visible().length;
    voorbeeld = true;
    renderResult();
  } else {
    state = {};
    index = 0;
    startRun();
    renderQuestion();
  }
  requestAnimationFrame(() => { win.dataset.open = 'true'; });
  const hash = view === 'example' ? EXAMPLE : HASH;
  pushed = Boolean(push && location.hash !== hash);
  if (pushed) history.pushState({ auSkinAnalysis: true }, '', hash);
}

function closeAnalysis(fromHistory = false) {
  if (wachten) { clearTimeout(wachten); wachten = null; }
  if (!win || win.hidden) return;
  win.dataset.open = 'false';
  win.hidden = true;
  chatLift(null);
  document.documentElement.classList.remove('au-sa-lock');
  if (!fromHistory && (location.hash === HASH || location.hash === EXAMPLE)) {
    if (pushed) history.back();
    else history.replaceState(history.state, '', location.pathname + location.search);
  }
  pushed = false;
  if (returnFocus instanceof HTMLElement) returnFocus.focus({ preventScroll: true });
}

/** Is dit een link naar deze pagina? */
function pointsHere(anchor) {
  try {
    const url = new URL(anchor.href, location.href);
    if (url.origin !== location.origin) return false;
    const strip = (path) => path.replace(/\/+$/, '');
    return strip(url.pathname) === strip(location.pathname) || /\/pages\/skin-analysis\/?$/.test(url.pathname);
  } catch {
    return false;
  }
}

if (ROOT && CFG) {
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.au-sa')) return;
    const opener = target.closest('[data-au-sa-open]');
    if (opener) { event.preventDefault(); openAnalysis(); return; }
    const anchor = target.closest('a[href]');
    if (!anchor || !anchor.closest('#MainContent')) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!pointsHere(anchor)) return;
    event.preventDefault();
    openAnalysis();
  });

  window.addEventListener('popstate', () => {
    if (location.hash === HASH) openAnalysis(false);
    else if (location.hash === EXAMPLE) openAnalysis(false, 'example');
    else closeAnalysis(true);
  });

  if (location.hash === HASH) openAnalysis(false);
  else if (location.hash === EXAMPLE) openAnalysis(false, 'example');

  /* In de editor: sectie aanklikken opent de test of de voorbeelduitslag (instelling editor_view), weg klikken sluit
     hem. Na een wijziging in de instellingen tekent de editor de sectie opnieuw; dan lezen we de instellingen opnieuw
     en openen we hem weer zoals hij stond (31 oktober 2026). */
  const editorView = () => (CFG?.editorView === 'example' ? 'example' : undefined);
  const onze = (event) => (event.target instanceof Element ? event.target.querySelector('[data-au-sa-root]') : null);
  document.addEventListener('shopify:section:select', (event) => {
    if (onze(event)) openAnalysis(false, editorView());
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (onze(event)) closeAnalysis(true);
  });
  document.addEventListener('shopify:section:load', (event) => {
    const root = onze(event);
    const nieuw = root && leesInstellingen(root);
    if (!nieuw) return;
    const wasOpen = Boolean(win && !win.hidden);
    if (win) {
      closeAnalysis(true);
      if (resultWatch) { resultWatch.disconnect(); resultWatch = null; }
      if (sideWatch) { sideWatch.disconnect(); sideWatch = null; }
      win.remove();
      win = null;
    }
    CFG = nieuw;
    gebruikInstellingen();
    if (wasOpen) openAnalysis(false, editorView());
  });
}
